// src/pages/ExpertReportGenerator.jsx
//
// Générateur de rapport d'expertise CUP pour l'Art Advisor
// Conforme au devis QUANG & CO :
//   Option 1 — Rapport global synthétique (2 000€)
//   Option 2 — 60 fiches CUP individuelles (3 500€)
//   Option 3 — 200 fiches CUP (7 800€)
//
// Branchement dans App.js :
//   import ExpertReportGenerator from "./pages/ExpertReportGenerator";
//   <Route path="/expert-report" element={<ExpertReportGenerator />} />
//
// Bouton dans CollectionDetail ou Dashboard Art Advisor :
//   <Link to="/expert-report">Générer le rapport d'expertise</Link>

import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { valuationFooter } from "../utils/valuationDisclosure";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, FileText, Download, CheckCircle2,
  AlertCircle, Loader2, BarChart2, FileSpreadsheet, FileDown,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────



// jsPDF (helvetica intégrée) n'accepte que le latin-1 (ISO-8859-1).
// Ce helper remplace les caractères hors-latin1 courants pour éviter les "?" et boîtes.
function sanitize(str) {
  if (!str) return "";
  return String(str)
    .replace(/\u2019/g, "'")
    .replace(/\u2018/g, "'")
    .replace(/\u201C/g, '"')
    .replace(/\u201D/g, '"')
    .replace(/\u2013/g, "-")
    .replace(/\u2014/g, "--")
    .replace(/\u2026/g, "...")
    .replace(/\u00e6/g, "ae")
    .replace(/\u0153/g, "oe")
    .replace(/\u0152/g, "OE")
    .replace(/\u00d7/g, "x")       // × → x
    .replace(/\u20ac/g, "EUR")     // € → EUR
    .replace(/\u2212/g, "-")       // − → -
    .replace(/\u2011/g, "-")       // ‑ → -
    // \u00b0 (°) conservé : latin-1 le supporte, jsPDF l'affiche correctement
    .replace(/\u00ab/g, "<<")      // « → <<
    .replace(/\u00bb/g, ">>")      // » → >>
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\xFF]/g, "?");
}

// Traduit les clés canoniques medium en libellés français lisibles
const MEDIUM_FR = {
  oil_on_canvas:         "Huile sur toile",
  oil_on_panel:          "Huile sur panneau",
  oil_on_paper:          "Huile sur papier",
  acrylic_on_canvas:     "Acrylique sur toile",
  acrylic_on_paper:      "Acrylique sur papier",
  acrylic_on_panel:      "Acrylique sur panneau",
  watercolor:            "Aquarelle",
  gouache:               "Gouache",
  ink_on_paper:          "Encre sur papier",
  ink_on_canvas:         "Encre sur toile",
  pencil_on_paper:       "Crayon sur papier",
  charcoal_on_paper:     "Fusain sur papier",
  pastel_on_paper:       "Pastel sur papier",
  mixed_media:           "Techniques mixtes",
  mixed_media_on_canvas: "Techniques mixtes sur toile",
  mixed_media_on_paper:  "Techniques mixtes sur papier",
  collage:               "Collage",
  print:                 "Estampe",
  lithograph:            "Lithographie",
  serigraph:             "Serigraphie",
  etching:               "Eau-forte",
  engraving:             "Gravure",
  photography:           "Photographie",
  photography_generic:   "Photographie",
  c_print:               "Tirage C-print",
  lambda_print:          "Tirage lambda",
  silver_gelatin_print:  "Tirage argentique",
  pigment_print:         "Impression pigmentaire",
  digital_print:         "Impression numerique",
  sculpture:             "Sculpture",
  bronze:                "Bronze",
  marble:                "Marbre",
  ceramic:               "Ceramique",
  resin:                 "Resine",
  wood:                  "Bois",
  steel:                 "Acier",
  iron:                  "Fer",
  glass:                 "Verre",
  video:                 "Video",
  installation:          "Installation",
  drawing:               "Dessin",
  fresco:                "Fresque",
  tempera:               "Tempera",
  oil_on_linen:          "Huile sur lin",
  pencil_on_canvas:      "Crayon sur toile",
  crayon:                "Crayon",
  pen_and_ink:           "Plume et encre",
};

function decodeMedium(artwork, tr) {
  // 1. Override manuel toujours prioritaire
  if (artwork.medium_display_override) return artwork.medium_display_override;

  // 2. Si medium_normalized_key existe, on traduit via le dictionnaire
  if (artwork.medium_normalized_key) {
    const translated = MEDIUM_FR[artwork.medium_normalized_key];
    if (translated) return tr ? tr(`artwork.mediums.${artwork.medium_normalized_key}`, { defaultValue: translated }) : translated;
  }

  // 3. Si le champ medium brut ressemble à une clé snake_case (ex: "oil_on_canvas"),
  //    on essaie de le traduire — sinon on retourne le texte tel quel
  const raw = artwork.medium || "";
  if (raw && /^[a-z][a-z0-9_]+$/.test(raw.trim())) {
    return tr ? tr(`artwork.mediums.${raw.trim()}`, { defaultValue: MEDIUM_FR[raw.trim()] || raw }) : (MEDIUM_FR[raw.trim()] || raw);
  }

  return raw;
}

function formatEur(val) {
  if (!val && val !== 0) return "-";
  const n = Math.round(Number(val));
  // toLocaleString("fr-FR") produit des espaces insecables (U+202F / U+00A0)
  // que jsPDF encode en "&" dans le PDF. On formate manuellement.
  const abs = Math.abs(n);
  const s = String(abs).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return (n < 0 ? "-" : "") + s + " EUR";
}

function today(locale = "fr-FR") {
  return new Date().toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
}

// Extrait la valeur estimée retenue depuis toutes les sources possibles.
// EstimationSemblablesPage stocke le résultat dans cup_comparables (JSONB)
// sous la clé valuation_retained quand valuation_estimated_value est null.
function getCupObj(artwork) {
  const c = artwork.cup_comparables;
  if (!c) return null;
  if (typeof c === "string") {
    try { const o = JSON.parse(c); return (o && !Array.isArray(o)) ? o : null; }
    catch { return null; }
  }
  return (typeof c === "object" && !Array.isArray(c)) ? c : null;
}

function getEstVal(artwork) {
  if (artwork.valuation_estimated_value) return Number(artwork.valuation_estimated_value);
  if (artwork.current_estimated_value)   return Number(artwork.current_estimated_value);
  if (artwork.valuation_consensus)        return Number(artwork.valuation_consensus);
  if (artwork.valuation_market_value)     return Number(artwork.valuation_market_value);
  // Groupe C : résultat dans cup_comparables.valuation_retained (objet JSON)
  const obj = getCupObj(artwork);
  if (obj?.valuation_retained) return Number(obj.valuation_retained);
  // Anciennes sauvegardes (avant que current_estimated_value soit écrit) :
  // valuation_low === valuation_high = valeur retenue, ou moyenne des deux.
  const lo = artwork.valuation_low  ? Number(artwork.valuation_low)  : null;
  const hi = artwork.valuation_high ? Number(artwork.valuation_high) : null;
  if (lo && hi) return Math.round((lo + hi) / 2);
  if (lo) return lo;
  if (hi) return hi;
  return null;
}



function normalizeDocText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’‘]/g, "'");
}

function docRowToText(doc = {}) {
  return normalizeDocText([
    doc.title,
    doc.name,
    doc.filename,
    doc.file_name,
    doc.document_type,
    doc.doc_type,
    doc.type,
    doc.category,
    doc.kind,
    doc.notes,
    doc.description,
  ].filter(Boolean).join(" "));
}

function inferDocumentFlagsFromRows(docs = [], artwork = {}) {
  const artworkText = normalizeDocText([
    artwork.documents,
    artwork.certificate_authenticity,
    artwork.condition_report,
    artwork.provenance,
  ].filter(Boolean).join(" "));
  const text = `${artworkText} ${(docs || []).map(docRowToText).join(" ")}`;

  return {
    has_invoice: Boolean(
      artwork.invoice ||
      text.includes("invoice") ||
      text.includes("facture") ||
      text.includes("receipt") ||
      text.includes("recu")
    ),
    has_certificate: Boolean(
      artwork.certificate_auth ||
      text.includes("certificat") ||
      text.includes("certificate") ||
      text.includes("coa") ||
      text.includes("authenticite")
    ),
    has_condition_report: Boolean(
      artwork.condition_report ||
      text.includes("condition_report") ||
      text.includes("condition report") ||
      text.includes("constat") ||
      text.includes("etat de conservation") ||
      text.includes("rapport de conservation")
    ),
  };
}

function buildDocumentSummary(artwork = {}, tr) {
  const docs = artwork._document_rows || [];
  const flags = inferDocumentFlagsFromRows(docs, artwork);
  const parts = [];
  if (flags.has_invoice) parts.push(tr ? tr("documents.invoice") : "facture");
  if (flags.has_certificate) parts.push(tr ? tr("documents.certificate") : "certificat");
  if (flags.has_condition_report) parts.push(tr ? tr("documents.condition") : "constat / conservation");
  if (!parts.length && docs.length) parts.push(tr ? tr("documents.count", { count: docs.length }) : `${docs.length} document${docs.length > 1 ? "s" : ""}`);
  if (!parts.length) return null;
  return parts.join(" · ");
}

function getEstLow(artwork) {
  if (artwork.valuation_low) return Number(artwork.valuation_low);
  const obj = getCupObj(artwork);
  if (obj?.valuation_low) return Number(obj.valuation_low);
  const v = getEstVal(artwork);
  return v ? Math.round(v * 0.9) : null;
}

function getEstHigh(artwork) {
  if (artwork.valuation_high) return Number(artwork.valuation_high);
  const obj = getCupObj(artwork);
  if (obj?.valuation_high) return Number(obj.valuation_high);
  const v = getEstVal(artwork);
  return v ? Math.round(v * 1.1) : null;
}

function isCupComplete(artwork) {
  return !!getEstVal(artwork);
}

// ─── Génération Excel via SheetJS ─────────────────────────────────────────────

async function generateExcel(artworks, collectionTitle, tr, locale) {
  const XLSX = await import("xlsx");

  // Onglet 1 — Récapitulatif global
  const recap = artworks.map(aw => ({
    [tr("export.excel.inventory")]: aw.inventory_number || "",
    [tr("export.excel.title")]: aw.title || "",
    [tr("export.excel.artist")]: aw.artist || "",
    [tr("export.excel.year")]: aw.year || "",
    [tr("export.excel.medium")]: decodeMedium(aw, tr) || "",
    [tr("export.excel.dimensions")]: aw.dimensions || "",
    [tr("export.excel.estimated")]: getEstVal(aw) || "",
    [tr("export.excel.low")]: getEstLow(aw) || "",
    [tr("export.excel.high")]: getEstHigh(aw) || "",
    [tr("export.excel.method")]: aw.expert_method || "",
    [tr("export.excel.expertDate")]: aw.expert_date || "",
    [tr("export.excel.complete")]: isCupComplete(aw) ? tr("common.yes") : tr("common.no"),
  }));

  const ws1 = XLSX.utils.json_to_sheet(recap);
  const wb  = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, tr("export.sheets.recap"));

  // Onglet 2 — Détail comparables
  const details = [];
  artworks.forEach(aw => {
    const comps = Array.isArray(aw.cup_comparables) ? aw.cup_comparables : [];
    comps.forEach((c, i) => {
      details.push({
        [tr("export.excel.inventory")]: aw.inventory_number || "",
        [tr("export.excel.artworkTitle")]: aw.title || "",
        [tr("export.excel.artist")]: aw.artist || "",
        [tr("export.excel.comparableNo")]: i + 1,
        [tr("export.excel.auctionHouse")]: c.maison || "",
        [tr("export.excel.saleDate")]: c.date || "",
        [tr("export.excel.soldArtwork")]: c.titre || "",
        [tr("export.excel.hammer")]: c.prix_adjuge || "",
        [tr("export.excel.adjustment")]: c.ajustement || "",
        [tr("export.excel.note")]: c.note || "",
      });
    });
  });

  if (details.length) {
    const ws2 = XLSX.utils.json_to_sheet(details);
    XLSX.utils.book_append_sheet(wb, ws2, tr("export.sheets.comparables"));
  }

  // Onglet 3 — Synthèse par artiste
  // Regroupement par clé canonique pour éviter les doublons de casse
  // ex. « Valentin VAN DER MEULEN » et « Valentin van der MEULEN ».
  const byArtist = {};
  artworks.forEach(aw => {
    const rawArtist = String(aw.artist || tr("common.unattributed")).trim() || tr("common.unattributed");
    const artistKey = normalizeArtistKey(rawArtist) || "non attribue";
    if (!byArtist[artistKey]) byArtist[artistKey] = { artist: rawArtist, count: 0, total: 0 };
    byArtist[artistKey].count++;
    const v = getEstVal(aw);
    if (v) byArtist[artistKey].total += v;
  });

  const synthese = Object.entries(byArtist)
    .sort(([, dA], [, dB]) => artistSortKey(dA.artist).localeCompare(artistSortKey(dB.artist), locale, { sensitivity: "base" }))
    .map(([_, d]) => ({
      [tr("export.excel.artist")]: d.artist,
      [tr("export.excel.count")]: d.count,
      [tr("export.excel.total")]: d.total || "",
      [tr("export.excel.average")]: d.count && d.total ? Math.round(d.total / d.count) : "",
    }));

  const ws3 = XLSX.utils.json_to_sheet(synthese);
  XLSX.utils.book_append_sheet(wb, ws3, tr("export.sheets.artists"));

  // Téléchargement
  const slug = collectionTitle.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
  XLSX.writeFile(wb, `${tr("export.filePrefix")}_${slug}.xlsx`);
}

// ─── Génération PDF via jsPDF ─────────────────────────────────────────────────

// Traduit les clés méthode en texte lisible client
function methodLabel(method, tr) {
  if (!method) return null;
  const m = String(method).toLowerCase();
  if (m.includes("direct_cup") || m.includes("group_a") || m === "a") return tr ? tr("methods.direct") : "CUP directe — ventes aux enchères de l'artiste";
  if (m.includes("analogy_cup") || m.includes("group_b") || m === "b") return tr ? tr("methods.analogy") : "CUP par analogie — artistes de profil similaire";
  if (m.includes("point") || m.includes("group_c") || m === "c") return tr ? tr("methods.points") : "Méthode par points — marché primaire encadré";
  return null;
}

// Traduit la note technique interne en phrase lisible
function translateNote(note) {
  if (!note) return null;
  let n = String(note);
  // Supprimer les formules points brutes type "(120 + 150) × 10-10 EUR/pt"
  n = n.replace(/Groupe\s+C\s+--\s+\([\d\s+]+\)\s*[x×]\s*[\d\s\-+EUR/pt]+/gi, "");
  n = n.replace(/Groupe\s+[ABC][+]?\s+--?\s*/gi, "");
  n = n.replace(/\([\d.]+\s*\+\s*[\d.]+\)\s*[x×]\s*[\d\-\s\u20AC/pt]+/g, "");
  n = n.replace(/EUR\/pt/g, "").replace(/\d+\s*-\s*\d+\s*EUR\/pt/g, "");
  // Remplacer symboles non-latin1 résiduels
  n = n.replace(/×/g, "x").replace(/€/g, "EUR").replace(/[–−]/g, "-");
  // Nettoyer les résidus
  n = n.replace(/^\s*[-·]\s*/, "").replace(/\s{2,}/g, " ").trim();
  return n || null;
}

// Formate une date ISO ou partielle en "mois AAAA" lisible
function fmtDate(raw, locale = "fr-FR") {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw).substring(0, 10);
  return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

// Traduit le code storage_risk en libellé lisible
function storageRiskLabel(risk, tr) {
  if (!risk) return null;
  if (risk === "faible") return tr ? tr("risks.low") : "Risque faible";
  if (risk === "moyen") return tr ? tr("risks.medium") : "Risque moyen";
  if (risk === "\u00e9lev\u00e9" || risk === "élevé") return tr ? tr("risks.high") : "Risque eleve";
  return risk;
}

// Traduit le code condition (état de conservation) en libellé lisible pour le rapport
function conditionLabel(cond, tr) {
  // Mappe les clés canoniques de la base (fr.js condition.*)
  // ET les anciennes valeurs legacy françaises vers des libellés jsPDF-safe (sans accents spéciaux)
  if (!cond || cond.trim() === "") return null; // null = inconnu
  const c = cond.toLowerCase().trim();
  // Clés anglaises actuelles (stockées en base)
  if (c === "excellent")                          return tr ? tr("conditions.excellent") : "Excellent";
  if (c === "very_good")                          return tr ? tr("conditions.very_good") : "Tres bon / Tres bon etat";
  if (c === "good")                               return tr ? tr("conditions.good") : "Bon";
  if (c === "fair")                               return tr ? tr("conditions.fair") : "Moyen";
  if (c === "very_poor")                          return tr ? tr("conditions.very_poor") : "Tres moyen";
  if (c === "poor")                               return tr ? tr("conditions.poor") : "Mauvais";
  if (c === "damaged")                            return tr ? tr("conditions.damaged") : "Endommage";
  if (c === "heavily_damaged")                    return tr ? tr("conditions.heavily_damaged") : "Tres abime (restaurable)";
  if (c === "partially_restorable")               return tr ? tr("conditions.partially_restorable") : "Partiellement restaurable";
  if (c === "to_restore")                         return tr ? tr("conditions.to_restore") : "A restaurer";
  if (c === "irreparable" || c === "irrecuperable") return tr ? tr("conditions.irreparable") : "Irrecuperable";
  if (c === "unknown")                            return null; // traité comme inconnu
  // Legacy françaises (anciennes valeurs)
  if (c === "tres_bon" || c === "très bon" || c === "tres bon") return "Tres bon";
  if (c === "bon")                                return tr ? tr("conditions.good") : "Bon";
  if (c === "moyen")                              return tr ? tr("conditions.fair") : "Moyen";
  if (c === "tres_moyen" || c === "très moyen" || c === "tres moyen") return tr ? tr("conditions.very_poor") : "Tres moyen";
  if (c === "abime" || c === "abimé" || c === "tres_abime" || c === "très abimé")
                                                  return tr ? tr("conditions.heavily_damaged") : "Tres abime (restaurable)";
  if (c === "irréparable")                        return tr ? tr("conditions.irreparable") : "Irrecuperable";
  return null; // clé non reconnue → traité comme inconnu plutôt qu'afficher la clé brute
}

// Extrait la maison de ventes depuis auction_house ou source (fallback)
// source peut contenir "URL · Artnet records" ou "Artprice.com — artprice_pdf"
function extractMaison(c) {
  if (c.auction_house) return c.auction_house.trim();
  if (!c.source) return "";
  // Format typique: "https://... · NomMaison" ou "NomMaison — ..."
  const src = String(c.source);
  // Chercher après le dernier · ou ·
  const dotIdx = src.lastIndexOf("·");
  if (dotIdx >= 0) return src.substring(dotIdx + 1).trim().substring(0, 30);
  // Chercher avant le premier —
  const dashIdx = src.indexOf(" — ");
  if (dashIdx >= 0) return src.substring(0, dashIdx).replace(/https?:\/\/[^\s]+/g, "").trim().substring(0, 30);
  // Nettoyer URL pure
  return src.replace(/https?:\/\/[^\s]+/g, "").replace(/·/g, "").trim().substring(0, 30);
}


function normalizeArtistKey(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Tri par nom de famille : extrait le token en MAJUSCULES (ex: "Philippe PASQUA" → "pasqua")
// Pour les noms tout-majuscules (ex: "XIAO FAN", "DU ZHENJUN"), prend le premier token.
// Pour les noms mixtes (ex: "Bae Bien-U"), fallback sur le dernier token.
function artistSortKey(rawName) {
  const name = String(rawName || "").trim();

  // Nettoyer les traits d'union et caractères spéciaux pour la détection
  const cleanName = name.replace(/[-–—]/g, " ").trim();
  const tokens = cleanName.split(/\s+/).filter(Boolean);

  if (tokens.length === 0) return normalizeArtistKey(name);

  // Détecter si TOUS les tokens sont en majuscules (ex: "DU ZHENJUN", "XIAO FAN", "LI TIAN BIN")
  const allCaps = tokens.every(t => t === t.toUpperCase() && /[A-Z]/.test(t));
  if (allCaps) {
    // Nom tout-caps : prendre le premier token (nom de famille pour noms asiatiques)
    return tokens[0].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  // Chercher les tokens tout-majuscules (NOM dans "Prénom NOM")
  // Exclure les tokens d'1 seule lettre (initiales, "U" dans "Bien-U")
  const upperTokens = tokens.filter(t => /^[A-ZÀÂÄÉÈÊËÎÏÔÖÙÛÜŸÆŒ]{2,}$/.test(t));
  if (upperTokens.length > 0) {
    // Dernier token tout-majuscules de ≥2 chars = nom de famille
    const lastName = upperTokens[upperTokens.length - 1];
    return lastName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  // Fallback : si le nom original contenait un trait d'union dans le prénom (ex: "Bae Bien-U"),
  // prendre le premier token (qui est le nom de famille)
  // Sinon dernier token
  const hadHyphen = name.includes("-");
  const parts = normalizeArtistKey(name).split(" ").filter(Boolean);
  if (hadHyphen && parts.length >= 2) {
    return parts[0] || normalizeArtistKey(name);
  }
  return parts[parts.length - 1] || normalizeArtistKey(name);
}

function selectedComparableToReportRow(row, fallbackArtist = "", untitledLabel = "Untitled") {
  if (!row) return null;
  // `comparable` = objet joint depuis estimation_comparables via comparable_id (null pour extrapolations)
  const comp = row.comparable || {};
  return {
    title:          comp.title         || row.title_snapshot || row.title || untitledLabel,
    artist:         row.artist_snapshot || comp.artist        || row.artist || fallbackArtist || "",
    auction_house:  comp.auction_house || comp.source || "",
    sale_date:      comp.auction_date  || null,
    technique:      comp.medium        || "",
    dimensions_cm:  comp.dimensions    || "",
    comment:        comp.comment       || row.note || "",
    hammer_price:   row.selected_price || comp.hammer_price   || row.hammer_price || null,
    estimated_value: row.selected_price || null,
    note:           row.note || "",
    kind:           row.kind || "direct_sale",
    weight:         row.weight || 1,
    source_type:    "expert_memory",
    // tiebreaker pour dedup : id unique de la ligne
    _row_id:        row.id || (row.estimation_id + "::" + (row.comparable_id || row.title_snapshot || Math.random())),
  };
}

function dedupeReportComparables(rows) {
  const map = new Map();
  (rows || []).filter(Boolean).forEach(row => {
    const key = [
      row._row_id || "",
      normalizeArtistKey(row.artist),
      String(row.title || "").toLowerCase().trim(),
      String(row.hammer_price || row.estimated_value || ""),
      String(row.auction_house || "").toLowerCase().trim(),
    ].join("::");
    if (!map.has(key)) map.set(key, row);
  });
  return Array.from(map.values());
}

function firstValidImageUrl(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // images peut parfois arriver en JSON stringifié.
    if ((trimmed.startsWith("[") && trimmed.endsWith("]")) || (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
      try { return firstValidImageUrl(JSON.parse(trimmed)); }
      catch { return trimmed; }
    }
    return trimmed;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstValidImageUrl(item);
      if (found) return found;
    }
    return null;
  }

  if (typeof value === "object") {
    return firstValidImageUrl(
      value.url ||
      value.publicUrl ||
      value.public_url ||
      value.signedUrl ||
      value.signed_url ||
      value.path ||
      value.src ||
      value.image_url
    );
  }

  return null;
}

function getArtworkMainImageUrl(artwork = {}) {
  // Colonnes réellement disponibles dans public.artworks :
  // image_url, images (jsonb array), primary_image_path.
  // Priorité : 1) image isPrimary:true dans le tableau images
  //            2) image_url (colonne racine)
  //            3) primary_image_path
  //            4) première image du tableau images

  // 1. Chercher l'image marquée isPrimary dans le tableau images
  if (artwork.images) {
    const imgs = Array.isArray(artwork.images)
      ? artwork.images
      : (() => { try { return JSON.parse(artwork.images); } catch { return []; } })();
    const primary = imgs.find(img => img && img.isPrimary === true);
    if (primary) {
      const url = firstValidImageUrl(primary);
      if (url) return url;
    }
    // 4. fallback : première image du tableau
    if (imgs.length > 0) {
      const url = firstValidImageUrl(imgs[0]);
      if (url) return url;
    }
  }

  // 2. image_url colonne racine
  if (artwork.image_url) return artwork.image_url;

  // 3. primary_image_path
  if (artwork.primary_image_path) return artwork.primary_image_path;

  return null;
}

function imageFitBox(imgW, imgH, maxW, maxH) {
  if (!imgW || !imgH) return { w: maxW, h: maxH };
  const ratio = Math.min(maxW / imgW, maxH / imgH);
  return {
    w: Math.max(1, imgW * ratio),
    h: Math.max(1, imgH * ratio),
  };
}

async function createPdfThumbnail(url, maxPx = 520, quality = 0.58) {
  if (!url) return null;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.referrerPolicy = "no-referrer";

    img.onload = () => {
      try {
        const ratio = Math.min(1, maxPx / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round((img.naturalWidth || img.width) * ratio));
        canvas.height = Math.max(1, Math.round((img.naturalHeight || img.height) * ratio));
        const ctx = canvas.getContext("2d", { alpha: false });
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          dataUrl: canvas.toDataURL("image/jpeg", quality),
          width: canvas.width,
          height: canvas.height,
        });
      } catch (e) {
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = url;
  });
}


function buildExpertNarrative(aw, retainedComparables = [], fallbackComparables = [], artistQuotes = [], tr) {
  const estVal = getEstVal(aw);
  const estLow = getEstLow(aw);
  const estHigh = getEstHigh(aw);
  const parts = [];

  const methodRaw = String(aw?.expert_method || "").toLowerCase();
  const isGroupA = methodRaw.includes("direct_cup") || methodRaw.includes("group_a") || methodRaw === "a";
  const isGroupC = methodRaw.includes("point") || methodRaw.includes("group_c") || methodRaw === "c";
  // Tout ce qui n'est pas A ni C est Groupe B (analogy_cup, hybrid, etc.)

  const method = methodLabel(aw?.expert_method, tr);
  if (method) parts.push(tr("narrative.method", { method }));

  if (isGroupC) {
    // Groupe C : méthode par points — pas de ventes d'enchères, formule points.
    // On cherche la quote la plus pertinente pour cette œuvre (par medium_key),
    // plutôt que de prendre systématiquement artistQuotes[0] qui peut être
    // une quote d'un autre segment (problème de répétition pour Onodera, etc.)
    const awMediumKey = String(aw?.medium_key || aw?.medium || "").toLowerCase().replace(/\s+/g, "_");
    const matchedQuote = artistQuotes.find(q => {
      const qKey = String(q.medium_key || q.medium || "").toLowerCase().replace(/\s+/g, "_");
      return qKey && awMediumKey && qKey.includes(awMediumKey.substring(0, 8));
    }) || artistQuotes[0] || null;

    if (matchedQuote) {
      // Afficher la fourchette segment depuis la quote (price_low/price_high),
      // jamais la note générique identique pour toutes les œuvres.
      const qLow  = matchedQuote.price_low  ? Number(matchedQuote.price_low)  : null;
      const qHigh = matchedQuote.price_high ? Number(matchedQuote.price_high) : null;
      if (qLow && qHigh) {
        parts.push(tr("narrative.pointsRange", { segment: sanitize(matchedQuote.medium || ""), low: formatEur(qLow), high: formatEur(qHigh) }));
      }
    }
    // Valeur retenue sur l'œuvre (depuis cup_comparables / valuation_*)
    if (estLow && estHigh && estLow !== estHigh) {
      parts.push(tr("narrative.retainedRange", { low: formatEur(estLow), high: formatEur(estHigh) }));
    }
    if (estVal) parts.push(tr("narrative.retainedValue", { value: formatEur(estVal) }));
    // Note générique de liquidité : une seule phrase courte, pas la note complète
    parts.push(tr("narrative.groupC"));
    return parts.join(" ");
  }

  // CUP directe (personal_quote / direct_cup) : fourchette dans artist_quotes, pas de ventes individuelles
  const isDirectCup = methodRaw.includes("personal_quote") || methodRaw.includes("direct_cup");
  if (isDirectCup) {
    if (artistQuotes.length > 0) {
      const q = artistQuotes[0];
      const aqLow = q.price_low ? formatEur(Number(q.price_low)) : null;
      const aqHigh = q.price_high ? formatEur(Number(q.price_high)) : null;
      if (aqLow && aqHigh) parts.push(tr("narrative.referenceRange", { low: aqLow, high: aqHigh }));
      if (q.notes) parts.push(q.notes);
    }
    if (estLow && estHigh) parts.push(tr("narrative.retainedRange", { low: formatEur(estLow), high: formatEur(estHigh) }));
    if (estVal) parts.push(tr("narrative.retainedValue", { value: formatEur(estVal) }));
    return parts.join(" ");
  }

  // Groupe A ou B avec ventes individuelles — UNIQUEMENT retainedComparables (pas de fallback)
  const rows = (retainedComparables || []).filter(Boolean).slice(0, 8);

  if (rows.length > 0) {
    const counts = rows.reduce((acc, row) => {
      const kind = row.kind || "direct_sale";
      if (kind === "extrapolation") acc.extrapolations += 1;
      else if (kind === "analogy") acc.analogies += 1;
      else acc.directes += 1;
      return acc;
    }, { directes: 0, extrapolations: 0, analogies: 0 });

    if (isGroupA) {
      parts.push(tr("narrative.directSales", { count: counts.directes }));
    } else {
      parts.push(tr("narrative.selectedSales", { direct: counts.directes, extrapolations: counts.extrapolations, analogies: counts.analogies }));
    }
    parts.push(tr("narrative.references"));
    rows.forEach((row, index) => {
      const kindLabel = row.kind === "extrapolation" ? tr("narrative.kinds.extrapolation")
        : row.kind === "analogy" ? tr("narrative.kinds.analogy") : tr("narrative.kinds.direct");
      const artist = row.artist || row.artist_snapshot || aw?.artist || tr("narrative.artistMissing");
      const title = row.title || row.title_snapshot || tr("common.untitled");
      const source = row.auction_house || row.source_snapshot || row.source || tr("narrative.sourceMissing");
      const price = Number(row.hammer_price || row.selected_price || row.estimated_value || 0);
      const priceLabel = price ? formatEur(price) : tr("narrative.priceMissing");
      const weight = row.weight ? tr("narrative.weight", { weight: row.weight }) : "";
      parts.push(tr("narrative.row", { index: index + 1, kind: kindLabel, artist, title, source, price: priceLabel, weight }));
    });
  } else if (isGroupA) {
    parts.push(tr("narrative.pendingSales"));
  }
  // Groupe B sans ventes : pas de message — la cotation expertale suffit.

  if (estLow && estHigh) parts.push(tr("narrative.retainedRange", { low: formatEur(estLow), high: formatEur(estHigh) }));
  if (estVal) parts.push(tr("narrative.retainedValue", { value: formatEur(estVal) }));

  return parts.join(" ");
}

// ─── Génération Word via docx-js ──────────────────────────────────────────────

async function generateWord(artworks, collectionTitle, clientName, mode, comparablesParArtiste = {}, selectedComparablesParArtiste = {}, artistQuotesParArtiste = {}, tr, locale) {
  const {
    Document, Packer, Paragraph, TextRun,
    Table, TableRow, TableCell, WidthType, AlignmentType,
    BorderStyle, ShadingType, PageBreak,
  } = await import("docx");

  const slug = collectionTitle.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);

  // ── Helpers texte ────────────────────────────────────────────────────────────
  const fmtEurW = (v) => {
    if (!v && v !== 0) return "-";
    const n = Math.round(Number(v));
    return String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " EUR";
  };

  const fmtDateW = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw).substring(0, 10);
    return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
  };

  // Couleurs
  const GOLD_HEX  = "C9A961";
  const BLACK_HEX = "1A1A1A";
  const GRAY_HEX  = "888888";

  // ── Page de garde ────────────────────────────────────────────────────────────
  const coverChildren = [
    new Paragraph({ text: "", spacing: { before: 2400 } }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.coverTitle"), bold: true, size: 40, color: BLACK_HEX, font: "Georgia" })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.coverSubtitle"), size: 24, color: GRAY_HEX, font: "Georgia" })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 200 },
    }),
    new Paragraph({ text: "", spacing: { before: 600 } }),
    new Paragraph({
      children: [new TextRun({ text: clientName || collectionTitle, bold: true, size: 28, color: BLACK_HEX })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: collectionTitle, size: 20, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 120 },
    }),
    new Paragraph({ text: "", spacing: { before: 2400 } }),
    new Paragraph({
      children: [new TextRun({ text: tr("export.company"), size: 18, color: GOLD_HEX, bold: true })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: "231 rue Saint-Honoré, 75001 Paris", size: 16, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: new Date().toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" }), size: 16, color: GRAY_HEX })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 100 },
    }),
  ];

  // ── Corps du rapport par artiste ─────────────────────────────────────────────
  const ficheArtworks = mode === "synthese"
    ? artworks.filter(isCupComplete).slice(0, 20)
    : mode === "cup_60"
    ? artworks.filter(isCupComplete).slice(0, 60)
    : artworks.filter(isCupComplete);

  const ficheByArtist = {};
  ficheArtworks.forEach(aw => {
    const rawArtist = String(aw.artist || tr("common.unattributed")).trim() || tr("common.unattributed");
    const key = normalizeArtistKey(rawArtist) || "non attribue";
    if (!ficheByArtist[key]) ficheByArtist[key] = { artist: rawArtist, works: [] };
    ficheByArtist[key].works.push(aw);
  });

  const bodyChildren = [];

  Object.entries(ficheByArtist)
    .sort(([, gA], [, gB]) => artistSortKey(gA.artist).localeCompare(artistSortKey(gB.artist), "fr", { sensitivity: "base" }))
    .forEach(([artistKeyLc, group]) => {
      const { artist, works } = group;
      const artistQuotes = artistQuotesParArtiste[artistKeyLc] || [];
      const selectedComps = selectedComparablesParArtiste[artistKeyLc] || [];
      const comps = selectedComps.length ? selectedComps : (comparablesParArtiste[artistKeyLc] || []);

      const firstWork = works.find(w => w.expert_method) || works[0];
      const mLabel = methodLabel(firstWork?.expert_method, tr);
      const firstMethodRaw = String(firstWork?.expert_method || "").toLowerCase();
      const grpIsC = firstMethodRaw.includes("point") || firstMethodRaw.includes("group_c") || firstMethodRaw === "c";
      const grpIsA = firstMethodRaw.includes("direct_cup") || firstMethodRaw.includes("group_a") || firstMethodRaw === "a";

      const lotIds = [...new Set(works.map(w => w.lot_id).filter(Boolean))];
      const lotInfo = lotIds.length > 0 ? `  |  ${lotIds.length} lot${lotIds.length > 1 ? "s" : ""}` : "";

      // Titre artiste (page break sauf premier)
      bodyChildren.push(new Paragraph({
        children: [new PageBreak()],
      }));
      bodyChildren.push(new Paragraph({
        children: [
          new TextRun({ text: artist.toUpperCase(), bold: true, size: 28, color: "FFFFFF", font: "Helvetica Neue" }),
          new TextRun({ text: `   ${works.length} oeuvre${works.length > 1 ? "s" : ""} dans la collection${lotInfo}`, size: 18, color: "CCCCCC" }),
        ],
        shading: { type: ShadingType.SOLID, color: BLACK_HEX },
        spacing: { before: 0, after: 200 },
        indent: { left: 200 },
      }));

      // Méthode retenue
      if (mLabel) {
        bodyChildren.push(new Paragraph({
          children: [
            new TextRun({ text: tr("export.sections.method"), bold: true, size: 18, color: BLACK_HEX }),
            new TextRun({ text: mLabel, size: 18, color: GRAY_HEX }),
          ],
          shading: { type: ShadingType.SOLID, color: "F5F1EA" },
          spacing: { before: 100, after: 200 },
          indent: { left: 160 },
        }));
      }

      // Fourchettes retenues (artist_quotes)
      if (artistQuotes.length > 0) {
        bodyChildren.push(new Paragraph({
          children: [new TextRun({ text: tr("export.sections.ranges"), bold: true, size: 20, color: BLACK_HEX })],
          spacing: { before: 200, after: 80 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: GOLD_HEX } },
        }));
        artistQuotes.forEach(q => {
          const lo = q.price_low ? fmtEurW(q.price_low) : null;
          const hi = q.price_high ? fmtEurW(q.price_high) : null;
          const avg = q.average_value ? fmtEurW(q.average_value) : null;
          const rangeStr = lo && hi ? `${lo} - ${hi}` : (lo || hi || "-");
          bodyChildren.push(new Paragraph({
            children: [
              new TextRun({ text: q.medium || q.medium_key || "", bold: true, size: 18, color: BLACK_HEX }),
              new TextRun({ text: `    Fourchette : ${rangeStr}${avg ? `  |  Valeur centrale : ${avg}` : ""}`, size: 17, color: GOLD_HEX }),
            ],
            shading: { type: ShadingType.SOLID, color: "F9F7F4" },
            spacing: { before: 80, after: 80 },
            indent: { left: 160 },
          }));
        });
      }

      // Ventes de référence (A et B uniquement)
      if (!grpIsC && comps.length > 0) {
        const ventesTitle = grpIsA ? tr("export.sections.artistSales") : tr("export.sections.referenceSales");
        bodyChildren.push(new Paragraph({
          children: [new TextRun({ text: ventesTitle, bold: true, size: 20, color: BLACK_HEX })],
          spacing: { before: 300, after: 80 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: GOLD_HEX } },
        }));

        // Tableau des ventes
        const headerRow = new TableRow({
          children: [tr("export.table.artworkArtist"), tr("export.table.source"), tr("export.table.hammer")].map(h =>
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, size: 16, color: "FFFFFF" })] })],
              shading: { type: ShadingType.SOLID, color: BLACK_HEX },
              width: { size: h === tr("export.table.artworkArtist") ? 55 : h === tr("export.table.source") ? 30 : 15, type: WidthType.PERCENTAGE },
            })
          ),
        });

        const dataRows = comps.slice(0, 12).map((c, i) => {
          const cTitle  = c.title || c.title_snapshot || tr("common.untitled");
          const cArtist = c.artist || c.artist_snapshot || artist;
          const cMaison = extractMaison(c);
          const cDate   = fmtDateW(c.sale_date || c.created_at);
          const cHammer = parseFloat(c.hammer_price || c.selected_price || 0) || 0;
          const cEst    = parseFloat(c.estimated_value || 0) || 0;
          const prixStr = cHammer ? fmtEurW(cHammer) : cEst ? `Est. ${fmtEurW(cEst)}` : "—";
          const bg = i % 2 === 0 ? "FFFFFF" : "F9F7F4";
          return new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({ children: [new TextRun({ text: cTitle, bold: true, size: 16 })] }),
                  new Paragraph({ children: [new TextRun({ text: cArtist, size: 14, color: GRAY_HEX })] }),
                ],
                shading: { type: ShadingType.SOLID, color: bg },
                width: { size: 55, type: WidthType.PERCENTAGE },
              }),
              new TableCell({
                children: [
                  new Paragraph({ children: [new TextRun({ text: cMaison, size: 15 })] }),
                  new Paragraph({ children: [new TextRun({ text: cDate, size: 13, color: GRAY_HEX })] }),
                ],
                shading: { type: ShadingType.SOLID, color: bg },
                width: { size: 30, type: WidthType.PERCENTAGE },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: prixStr, bold: true, size: 16, color: GOLD_HEX })], alignment: AlignmentType.RIGHT })],
                shading: { type: ShadingType.SOLID, color: bg },
                width: { size: 15, type: WidthType.PERCENTAGE },
              }),
            ],
          });
        });

        bodyChildren.push(new Table({ rows: [headerRow, ...dataRows], width: { size: 100, type: WidthType.PERCENTAGE } }));
        bodyChildren.push(new Paragraph({ text: "", spacing: { after: 200 } }));
      }

      // Fiches individuelles
      works.forEach((aw, idx) => {
        bodyChildren.push(new Paragraph({ children: [new PageBreak()] }));

        const ficheNum = ficheArtworks.indexOf(aw) + 1;
        const awMethodRaw = String(aw?.expert_method || "").toLowerCase();
        const awIsGroupC = awMethodRaw.includes("point") || awMethodRaw.includes("group_c") || awMethodRaw === "c";
        const awIsGroupA = awMethodRaw.includes("direct_cup") || awMethodRaw.includes("group_a") || awMethodRaw === "a";
        const estVal  = getEstVal(aw);
        const estLow  = getEstLow(aw);
        const estHigh = getEstHigh(aw);

        // En-tête fiche
        bodyChildren.push(new Paragraph({
          children: [
            new TextRun({ text: `FICHE N\xb0${ficheNum} - ${aw.inventory_number || aw.id.substring(0, 8)}`, bold: true, size: 20, color: "FFFFFF" }),
            new TextRun({ text: `   ${new Date().toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" })} - QUANG & CO`, size: 16, color: "AAAAAA" }),
          ],
          shading: { type: ShadingType.SOLID, color: BLACK_HEX },
          spacing: { after: 200 },
          indent: { left: 200 },
        }));

        // Titre oeuvre
        bodyChildren.push(new Paragraph({
          children: [new TextRun({ text: aw.title || tr("common.untitled"), bold: true, size: 28, color: BLACK_HEX, font: "Georgia" })],
          spacing: { before: 100, after: 60 },
        }));
        bodyChildren.push(new Paragraph({
          children: [new TextRun({
            text: [aw.artist, [aw.year, decodeMedium(aw, tr), aw.dimensions].filter(Boolean).join(" — ")].filter(Boolean).join("  /  "),
            size: 18, color: GRAY_HEX,
          })],
          spacing: { after: 200 },
        }));

        // Valeur encadrée
        bodyChildren.push(new Paragraph({
          children: [new TextRun({ text: tr("export.sections.marketValue"), bold: true, size: 17, color: BLACK_HEX })],
          shading: { type: ShadingType.SOLID, color: "FFFBF0" },
          border: { top: { style: BorderStyle.SINGLE, size: 4, color: GOLD_HEX }, bottom: { style: BorderStyle.SINGLE, size: 4, color: GOLD_HEX }, left: { style: BorderStyle.SINGLE, size: 4, color: GOLD_HEX }, right: { style: BorderStyle.SINGLE, size: 4, color: GOLD_HEX } },
          spacing: { before: 80, after: 0 },
          indent: { left: 160 },
        }));
        bodyChildren.push(new Paragraph({
          children: [
            new TextRun({ text: fmtEurW(estVal), bold: true, size: 36, color: GOLD_HEX }),
            estLow && estHigh ? new TextRun({ text: `    Fourchette : ${fmtEurW(estLow)} - ${fmtEurW(estHigh)}`, size: 18, color: GRAY_HEX }) : new TextRun({ text: "" }),
          ],
          shading: { type: ShadingType.SOLID, color: "FFFBF0" },
          border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: GOLD_HEX }, left: { style: BorderStyle.SINGLE, size: 4, color: GOLD_HEX }, right: { style: BorderStyle.SINGLE, size: 4, color: GOLD_HEX } },
          spacing: { before: 0, after: 200 },
          indent: { left: 160 },
        }));

        // ── État de conservation (Word) ──────────────────────────────────────
        {
          const condLblW = conditionLabel(aw.condition, tr);
          const condTextW = condLblW
            ? `ETAT DE CONSERVATION : ${condLblW}`
            : tr("export.sections.unknownCondition");
          const isUnknownW = !condLblW;
          bodyChildren.push(new Paragraph({
            children: [new TextRun({
              text: condTextW,
              bold: isUnknownW,
              italics: !isUnknownW,
              size: 16,
              color: isUnknownW ? "A05028" : GRAY_HEX,
            })],
            spacing: { before: 80, after: 160 },
          }));
        }

        // Méthode
        const mLabelFiche = methodLabel(aw.expert_method, tr);
        if (mLabelFiche) {
          bodyChildren.push(new Paragraph({
            children: [new TextRun({ text: mLabelFiche, italics: true, size: 16, color: GRAY_HEX })],
            spacing: { after: 160 },
          }));
        }

        // Note d'expert
        const ownArtistKey = normalizeArtistKey(aw.artist);
        const selectedRowsForWork = selectedComparablesParArtiste[ownArtistKey] || [];
        const fallbackRowsForWork = (comparablesParArtiste[ownArtistKey] || []).slice(0, 5);
        const aqForWork = artistQuotesParArtiste[ownArtistKey] || [];
        const expertNarrative = buildExpertNarrative(aw, selectedRowsForWork, fallbackRowsForWork, aqForWork, tr);
        if (expertNarrative) {
          bodyChildren.push(new Paragraph({ children: [new TextRun({ text: tr("export.sections.expertNote"), bold: true, size: 18, color: BLACK_HEX })], spacing: { before: 160, after: 60 } }));
          bodyChildren.push(new Paragraph({ children: [new TextRun({ text: expertNarrative, size: 16, color: GRAY_HEX })], spacing: { after: 160 } }));
        }

        const docSummary = buildDocumentSummary(aw, tr);
        if (docSummary) {
          bodyChildren.push(new Paragraph({
            children: [new TextRun({ text: tr("export.sections.documentation"), bold: true, size: 18, color: BLACK_HEX })],
            spacing: { before: 120, after: 60 },
          }));
          bodyChildren.push(new Paragraph({
            children: [new TextRun({ text: docSummary, size: 16, color: GRAY_HEX })],
            spacing: { after: 160 },
            indent: { left: 160 },
          }));
        }

        // Artistes analogues (B uniquement)
        const cupObj = getCupObj(aw);
        const analogArtistsRaw = cupObj?.analog_artists || aqForWork.flatMap(q => Array.isArray(q.analog_artists) ? q.analog_artists : []);
        if (!awIsGroupA && !awIsGroupC && analogArtistsRaw.length > 0) {
          bodyChildren.push(new Paragraph({ children: [new TextRun({ text: tr("export.sections.analogArtists"), bold: true, size: 18, color: BLACK_HEX })], spacing: { before: 120, after: 60 } }));
          bodyChildren.push(new Paragraph({ children: [new TextRun({ text: [...new Set(analogArtistsRaw)].join(", "), italics: true, size: 16, color: GRAY_HEX })], spacing: { after: 160 } }));
        }

        // COMMENTAIRE COMPLÉMENTAIRE supprimé — contenu fusionné dans NOTE DE L'EXPERT

        // ── Localisation et stockage (Word) ──────────────────────────────────
        const wLocName   = aw.locations?.name || aw.location_name || null;
        const wLocDetail = aw.storage_location_detail || null;
        const wLocRisk   = storageRiskLabel(aw.storage_risk, tr);
        const wLocVisit  = aw.visited_at ? fmtDateW(aw.visited_at) : null;

        if (wLocName || wLocDetail || wLocRisk) {
          bodyChildren.push(new Paragraph({
            children: [new TextRun({ text: tr("export.sections.storage"), bold: true, size: 18, color: BLACK_HEX })],
            spacing: { before: 160, after: 60 },
          }));
          const locParts = [
            wLocName && new TextRun({ text: wLocName, bold: true, size: 16, color: BLACK_HEX }),
            wLocRisk && new TextRun({ text: `   [${wLocRisk}]`, size: 15, color: GRAY_HEX }),
            wLocVisit && new TextRun({ text: `   Visite : ${wLocVisit}`, size: 14, color: GRAY_HEX }),
          ].filter(Boolean);
          bodyChildren.push(new Paragraph({
            children: locParts,
            shading: { type: ShadingType.SOLID, color: "F9F7F4" },
            spacing: { before: 60, after: wLocDetail ? 0 : 160 },
            indent: { left: 160 },
          }));
          if (wLocDetail) {
            bodyChildren.push(new Paragraph({
              children: [new TextRun({ text: wLocDetail, italics: true, size: 15, color: GRAY_HEX })],
              shading: { type: ShadingType.SOLID, color: "F9F7F4" },
              spacing: { before: 0, after: 160 },
              indent: { left: 160 },
            }));
          }
        }

        // Pied de page
        bodyChildren.push(new Paragraph({
          children: [new TextRun({ text: `${valuationFooter(artworks, String(locale).slice(0, 2))} ${tr("export.disclaimer")}`, size: 13, color: "AAAAAA" })],
          spacing: { before: 400 },
        }));
      });
    });

  // ── Assemblage document ───────────────────────────────────────────────────────
  const doc = new Document({
    sections: [{
      children: [...coverChildren, new Paragraph({ children: [new PageBreak()] }), ...bodyChildren],
    }],
  });

  const buffer = await Packer.toBlob(doc);
  const url = URL.createObjectURL(buffer);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${tr("export.filePrefix")}_${slug}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}

// Tri alphanumérique naturel sur inventory_number (PP-1 < PP-2 < PP-10)
function sortByInventory(works) {
  return [...works].sort((a, b) => {
    const ia = a.inventory_number || "\uFFFF"; // sans n° → fin
    const ib = b.inventory_number || "\uFFFF";
    return ia.localeCompare(ib, "fr", { numeric: true, sensitivity: "base" });
  });
}

async function generatePdf(artworks, collectionTitle, clientName, mode, comparablesParArtiste = {}, selectedComparablesParArtiste = {}, artistQuotesParArtiste = {}, tr, locale) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const W = 210, margin = 20;
  const contentW = W - 2 * margin;
  let y = margin;

  const addPage = () => { doc.addPage(); y = margin; };
  const checkY = (needed = 20) => { if (y + needed > 280) addPage(); };

  // Couleurs Artkiv
  const BLACK  = [26, 26, 26];
  const GOLD   = [201, 169, 97];
  const GRAY   = [120, 120, 120];
  const LGRAY  = [240, 238, 234];

  // Miniatures PDF : on part des images HD Supabase, mais on les compresse
  // en JPEG basse définition avant insertion pour garder un PDF léger.
  const thumbnailCache = new Map();
  const getThumbForArtwork = async (artwork) => {
    const url = getArtworkMainImageUrl(artwork);
    if (!url) return null;
    if (!thumbnailCache.has(url)) {
      thumbnailCache.set(url, createPdfThumbnail(url, 520, 0.58));
    }
    return thumbnailCache.get(url);
  };

  // ── Page de garde ──────────────────────────────────────────────────────────
  doc.setFillColor(...LGRAY);
  doc.rect(0, 0, W, 297, "F");

  doc.setFillColor(...BLACK);
  doc.rect(0, 0, W, 60, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(tr("export.coverTitle"), margin, 28);
  doc.setFontSize(13);
  doc.setFont("helvetica", "normal");
  doc.text(sanitize(tr("export.coverDescription")), margin, 40);
  doc.setFontSize(10);
  doc.text(tr("export.coverMethod"), margin, 50);

  y = 80;
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(sanitize(`Collection : ${collectionTitle}`), margin, y);

  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(sanitize(`Client : ${clientName || "-"}`), margin, y);

  y += 8;
  doc.text(sanitize(`Date du rapport : ${today(locale)}`), margin, y);

  // Totaux
  const valued    = artworks.filter(a => getEstVal(a));
  const totalVal  = valued.reduce((s, a) => s + (getEstVal(a) || 0), 0);
  const cupDone   = artworks.filter(isCupComplete).length;

  y += 20;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, y, contentW, 50, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text(tr("export.stats.evaluated"), margin + 10, y + 12);
  doc.text(tr("export.stats.totalValue"), margin + contentW / 3 + 10, y + 12);
  doc.text(tr("export.stats.estimatedSheets"), margin + 2 * contentW / 3 + 10, y + 12);

  doc.setTextColor(...BLACK);
  doc.setFontSize(20);
  doc.text(String(valued.length), margin + 10, y + 32);
  doc.text(formatEur(totalVal), margin + contentW / 3 + 10, y + 32);
  doc.text(`${cupDone} / ${artworks.length}`, margin + 2 * contentW / 3 + 10, y + 32);

  y += 65;
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("QUANG & CO - Expert independant en evaluation d'oeuvres d'art", margin, y);
  doc.text("231 rue Saint-Honore, 75001 Paris - vanessa@quangartadvisory.com", margin, y + 5);

  // ── Synthèse par artiste ───────────────────────────────────────────────────
  doc.addPage();
  y = margin;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BLACK);
  doc.text(tr("export.summaryByArtist"), margin, y);
  y += 2;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.5);
  doc.line(margin, y + 2, margin + contentW, y + 2);
  y += 12;

  const byArtist = {};
  artworks.forEach(aw => {
    const rawArtist = String(aw.artist || tr("common.unattributed")).trim() || tr("common.unattributed");
    const key = normalizeArtistKey(rawArtist) || "non attribue";
    if (!byArtist[key]) byArtist[key] = { artist: rawArtist, count: 0, total: 0, works: [] };
    byArtist[key].count++;
    const v = getEstVal(aw);
    if (v) byArtist[key].total += v;
    byArtist[key].works.push(aw);
  });

  // En-tête tableau
  const cols = [70, 30, 45, 45];
  const headers = [tr("export.table.artist"), tr("export.table.count"), tr("export.table.totalValue"), tr("export.table.averageValue")];
  doc.setFillColor(...BLACK);
  doc.rect(margin, y, contentW, 8, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  let cx = margin + 3;
  headers.forEach((h, i) => { doc.text(h, cx, y + 5.5); cx += cols[i]; });
  y += 8;

  let rowAlt = false;
  const sortedArtists = Object.entries(byArtist).sort(([, dA], [, dB]) =>
    artistSortKey(dA.artist).localeCompare(artistSortKey(dB.artist), locale, { sensitivity: "base" })
  );

  sortedArtists.forEach(([artistKey, d]) => {
    const artist = d.artist || artistKey;
    checkY(8);
    if (rowAlt) { doc.setFillColor(248, 246, 242); doc.rect(margin, y, contentW, 7, "F"); }
    rowAlt = !rowAlt;

    doc.setTextColor(...BLACK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    cx = margin + 3;
    const row = [
      artist.substring(0, 35),
      String(d.count),
      d.total ? formatEur(d.total) : "—",
      d.count && d.total ? formatEur(Math.round(d.total / d.count)) : "—",
    ];
    row.forEach((v, i) => { doc.text(v, cx, y + 5); cx += cols[i]; });
    y += 7;
  });

  // ── Page de définitions méthodologiques ───────────────────────────────────
  doc.addPage();
  y = margin;

  doc.setFillColor(...BLACK);
  doc.rect(0, 0, W, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(tr("export.methodologyTitle"), margin, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(tr("export.methodologyBase"), W - margin - 50, 14);
  y = 32;

  // Intro
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);
  const introText = tr("export.methodologyIntro");
  doc.splitTextToSize(introText, contentW).forEach(l => { doc.text(l, margin, y); y += 5; });
  y += 6;

  // Définitions — 3 blocs
  const methodDefs = [
    {
      label: tr("export.groups.aTitle"),
      color: [30, 30, 30],
      icon: "A",
      lines: [tr("export.groups.a1"), tr("export.groups.a2"), tr("export.groups.a3"), tr("export.groups.a4")],
    },
    {
      label: tr("export.groups.bTitle"),
      color: [60, 40, 10],
      icon: "B",
      lines: [tr("export.groups.b1"), tr("export.groups.b2"), tr("export.groups.b3"), tr("export.groups.b4")],
    },
    {
      label: tr("export.groups.cTitle"),
      color: [10, 40, 60],
      icon: "C",
      lines: [tr("export.groups.c1"), tr("export.groups.c2"), tr("export.groups.c3"), tr("export.groups.c4")],
    },
  ];

  methodDefs.forEach(def => {
    checkY(40);
    // En-tête bloc
    doc.setFillColor(...def.color);
    doc.rect(margin, y, contentW, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(def.label, margin + 4, y + 7);
    y += 12;

    // Corps
    doc.setFillColor(248, 246, 242);
    doc.rect(margin, y, contentW, def.lines.length * 5.5 + 4, "F");
    doc.setTextColor(...BLACK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    def.lines.forEach(line => {
      const wrapped = doc.splitTextToSize("• " + line, contentW - 6);
      wrapped.forEach(l => { doc.text(l, margin + 4, y + 4); y += 5; });
    });
    y += 8;
  });

  // Grille d'ajustement simplifiée
  checkY(50);
  y += 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  doc.text(tr("export.adjustmentsTitle"), margin, y);
  y += 2;
  doc.setDrawColor(...GOLD);
  doc.line(margin, y + 1, margin + contentW, y + 1);
  y += 8;

  const adjustRows = [
    [tr("export.adjustments.excellent"), "0 %", tr("export.adjustments.none")],
    [tr("export.adjustments.fair"), "- 20 %", tr("export.adjustments.fairDesc")],
    [tr("export.adjustments.veryPoor"), "- 30 %", tr("export.adjustments.veryPoorDesc")],
    [tr("export.adjustments.restorable"), "- 50 %", tr("export.adjustments.restorableDesc")],
    [tr("export.adjustments.irreparable"), tr("export.adjustments.zero"), tr("export.adjustments.totalLoss")],
    [tr("export.adjustments.certificate"), "+ 5 %", tr("export.adjustments.documentBonus")],
    [tr("export.adjustments.artistProvenance"), "+ 2 %", tr("export.adjustments.documentBonus")],
  ];

  doc.setFillColor(...BLACK);
  doc.rect(margin, y, contentW, 7, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text(tr("export.adjustmentHeaders.criterion"), margin + 3, y + 4.5);
  doc.text(tr("export.adjustmentHeaders.impact"), margin + 75, y + 4.5);
  doc.text(tr("export.adjustmentHeaders.description"), margin + 105, y + 4.5);
  y += 7;

  let adjAlt = false;
  adjustRows.forEach(([critere, impact, desc]) => {
    if (adjAlt) { doc.setFillColor(248, 246, 242); doc.rect(margin, y, contentW, 6.5, "F"); }
    else { doc.setFillColor(255, 255, 255); doc.rect(margin, y, contentW, 6.5, "F"); }
    adjAlt = !adjAlt;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...BLACK);
    doc.text(sanitize(critere), margin + 3, y + 4.5);
    const impactColor = impact.startsWith("+") ? [30, 100, 30] : impact.startsWith("-") ? [150, 30, 30] : GRAY;
    doc.setTextColor(...impactColor);
    doc.setFont("helvetica", "bold");
    doc.text(impact, margin + 75, y + 4.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(sanitize(desc), margin + 105, y + 4.5);
    y += 6.5;
  });

  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  y += 6;
  doc.text(tr("export.fairValueDisclaimer"), margin, y);
  doc.text(tr("export.confidentialShort"), margin, 285);

  // ── Fiches CUP individuelles (si mode 2 ou 3) ─────────────────────────────
  if (mode !== "synthese") {
    const ficheArtworks = mode === "cup_60"
      ? artworks.filter(isCupComplete).slice(0, 60)
      : artworks.filter(isCupComplete);

    // Préchargement compressé des vignettes.
    // 520 px côté long suffit pour une image de 55-60 mm dans un A4 imprimable.
    await Promise.all(ficheArtworks.map(aw => getThumbForArtwork(aw).catch(() => null)));

    // Grouper par artiste avec clé canonique pour éviter les doublons de casse
    const ficheByArtist = {};
    ficheArtworks.forEach(aw => {
      const rawArtist = String(aw.artist || tr("common.unattributed")).trim() || tr("common.unattributed");
      const key = normalizeArtistKey(rawArtist) || "non attribue";
      if (!ficheByArtist[key]) ficheByArtist[key] = { artist: rawArtist, works: [] };
      ficheByArtist[key].works.push(aw);
    });

    for (const [artistKeyLc, group] of Object.entries(ficheByArtist)
      .sort(([, gA], [, gB]) => artistSortKey(gA.artist).localeCompare(artistSortKey(gB.artist), "fr", { sensitivity: "base" }))) {
      const artist = group.artist;
      const works = group.works;
      const selectedComps = selectedComparablesParArtiste[artistKeyLc] || [];
      const comps = selectedComps.length ? selectedComps : (comparablesParArtiste[artistKeyLc] || []);

      // ── Page "Note de marché" par artiste ───────────────────────────────
      doc.addPage();
      y = margin;

      // Bandeau artiste
      doc.setFillColor(...BLACK);
      doc.rect(0, 0, W, 22, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(sanitize(artist.toUpperCase()), margin, 13);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      // Compter les lots distincts parmi les oeuvres de cet artiste
      const lotIds = [...new Set(works.map(w => w.lot_id).filter(Boolean))];
      const lotInfo = lotIds.length > 0
        ? `  |  ${lotIds.length} lot${lotIds.length > 1 ? "s" : ""}`
        : "";
      doc.text(sanitize(`${works.length} oeuvre${works.length > 1 ? "s" : ""} dans la collection${lotInfo}`), W - margin - 80, 13);

      y = 30;

      // Méthode de valorisation retenue
      const firstWork = works.find(w => w.expert_method) || works[0];
      const mLabel = methodLabel(firstWork?.expert_method, tr);
      if (mLabel) {
        doc.setFillColor(248, 246, 242);
        doc.roundedRect(margin, y, contentW, 10, 1.5, 1.5, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...BLACK);
        doc.text(tr("export.sections.method"), margin + 4, y + 4);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...GRAY);
        doc.text(sanitize(mLabel), margin + 48, y + 4);
        y += 16;
      }

      // ── Cotation expertale (artist_quotes) ──────────────────────────────
      const artistQuotes = artistQuotesParArtiste[artistKeyLc] || [];
      if (artistQuotes.length > 0) {
        checkY(14);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...BLACK);
        doc.text(tr("export.sections.ranges"), margin, y);
        y += 2;
        doc.setDrawColor(...GOLD);
        doc.line(margin, y + 1, margin + contentW, y + 1);
        y += 8;

        // eslint-disable-next-line no-loop-func
        artistQuotes.forEach(q => {
          checkY(18);
          doc.setFillColor(248, 246, 242);
          doc.roundedRect(margin, y, contentW, 16, 1.5, 1.5, "F");

          // Médium / format
          const medLabel = sanitize(q.medium || q.medium_key || "");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLACK);
          doc.text(medLabel.substring(0, 55), margin + 4, y + 6);

          // Fourchette + moyenne
          const lo = q.price_low ? formatEur(q.price_low) : null;
          const hi = q.price_high ? formatEur(q.price_high) : null;
          const avg = q.average_value ? formatEur(q.average_value) : null;
          const rangeStr = lo && hi ? `${lo} - ${hi}` : (lo || hi || "-");
          const avgStr = avg ? `  |  Valeur centrale : ${avg}` : "";
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(150, 110, 50);
          doc.text(sanitize(`Fourchette : ${rangeStr}${avgStr}`), margin + 4, y + 12);

          // Méthode groupe
          const mGroup = q.method_group ? sanitize(methodLabel(q.method_group, tr) || q.method_group) : null;
          if (mGroup) {
            doc.setTextColor(...GRAY);
            doc.setFontSize(7);
            doc.text(mGroup.substring(0, 70), W - margin - 2, y + 6, { align: "right" });
          }

          // Artistes analogues
          const analogs = Array.isArray(q.analog_artists) ? q.analog_artists : [];
          if (analogs.length > 0) {
            doc.setTextColor(...GRAY);
            doc.setFontSize(7);
            doc.text(sanitize(tr("export.analogues", { names: analogs.join(", ") })).substring(0, 80), W - margin - 2, y + 12, { align: "right" });
          }

          y += 20;
        });
        y += 4;
      }

      // Détecter le groupe pour cet artiste (depuis la première oeuvre ayant une méthode)
      const firstWorkMethod = String((works.find(w => w.expert_method) || works[0])?.expert_method || "").toLowerCase();
      const grpIsA = firstWorkMethod.includes("direct_cup") || firstWorkMethod.includes("group_a") || firstWorkMethod === "a";
      const grpIsC = firstWorkMethod.includes("point") || firstWorkMethod.includes("group_c") || firstWorkMethod === "c";

      // Comparables de marché retenus — Groupe A/B UNIQUEMENT si pas direct_cup
      // Pour direct_cup la fourchette est dans artist_quotes, pas des ventes individuelles
      const grpIsDirectCup = firstWorkMethod.includes("personal_quote") || firstWorkMethod.includes("direct_cup");
      const compsToShow = (grpIsC || grpIsDirectCup) ? [] : comps;
      if (compsToShow.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...BLACK);
        const ventesTitle = grpIsA
          ? tr("export.sections.artistSales")
          : tr("export.sections.referenceSales");
        doc.text(ventesTitle, margin, y);
        y += 2;
        doc.setDrawColor(...GOLD);
        doc.line(margin, y + 1, margin + contentW, y + 1);
        y += 8;

        // En-tête
        doc.setFillColor(...BLACK);
        doc.rect(margin, y, contentW, 7, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text(tr("export.table.artwork"), margin + 2, y + 4.5);
        doc.text(tr("export.table.detail"), margin + 70, y + 4.5);
        doc.text(tr("export.table.hammer"), W - margin - 16, y + 4.5);
        y += 8;

        let rowAltC = false;
        // Fiche artiste : max 6 ventes, rowH compact 12mm (pas de commentaire, 2 lignes)
        // eslint-disable-next-line no-loop-func
        compsToShow.slice(0, 6).forEach(c => {
          const cMaison   = extractMaison(c);
          const rawD      = c.sale_date || c.auction_date || null;
          const cDate     = rawD ? fmtDate(rawD, locale) : "";
          const cTech     = c.medium || c.technique || "";
          const cDims     = c.dimensions || c.dimensions_cm || "";
          const cArtist   = c.artist || artist;
          const isAnalogue = cArtist.toLowerCase() !== (artist || "").toLowerCase();

          // Compact : 2 lignes fixes, pas de commentaire sur la fiche artiste
          const rowH = 12;

          checkY(rowH + 2);
          doc.setFillColor(rowAltC ? 248 : 255, rowAltC ? 246 : 255, rowAltC ? 242 : 255);
          doc.rect(margin, y, contentW, rowH, "F");
          rowAltC = !rowAltC;

          const fmtP = (v) => String(Math.round(Math.abs(Number(v)))).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " EUR";
          const prix = c.hammer_price ? fmtP(c.hammer_price) : (c.estimated_value ? `Est. ${fmtP(c.estimated_value)}` : "—");

          // Colonne gauche (0→68mm) : titre (bold) + artiste/analogie
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(...BLACK);
          doc.text(sanitize((c.title || tr("common.untitled")).substring(0, 35)), margin + 2, y + 4.5);
          doc.setFont(isAnalogue ? "helvetica" : "helvetica", isAnalogue ? "italic" : "normal");
          doc.setFontSize(6.5);
          doc.setTextColor(...GRAY);
          doc.text(sanitize(cArtist.substring(0, 32)), margin + 2, y + 9.5);

          // Colonne centrale (70→128mm) : technique · dimensions sur 1 ligne
          doc.setFont("helvetica", "normal");
          doc.setFontSize(6.5);
          doc.setTextColor(...GRAY);
          const techDimStr = [cTech, cDims].filter(Boolean).join(" · ");
          if (techDimStr) doc.text(sanitize(techDimStr.substring(0, 30)), margin + 70, y + 4.5);
          // Maison · date sur ligne 2
          const maisonDateStr = [cMaison, cDate].filter(Boolean).join(" · ");
          if (maisonDateStr) doc.text(sanitize(maisonDateStr.substring(0, 30)), margin + 70, y + 9.5);

          // Prix (extrême droite, ligne 1, or)
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);
          doc.setTextColor(150, 110, 50);
          doc.text(prix, W - margin - 2, y + 4.5, { align: "right" });

          y += rowH + 1;
        });
        y += 4;
      }

      // Alertes méthodologiques — déduites des notes expert (formules Groupe C nettoyées)
      // Pour Groupe C : une seule note depuis artist_quote, pas les expert_note répétées de chaque artwork.
      const firstWorkMR = String((works[0])?.expert_method || "").toLowerCase();
      const isArtistGrpC = firstWorkMR.includes("point") || firstWorkMR.includes("group_c") || firstWorkMR === "c";
      let noteLines;
      if (isArtistGrpC) {
        const artistKeyGC = normalizeArtistKey(works[0]?.artist);
        const aqGC = (artistQuotesParArtiste[artistKeyGC] || [])[0];
        const aqNoteGC = aqGC?.notes ? translateNote(aqGC.notes) : null;
        noteLines = aqNoteGC ? [aqNoteGC] : [];
      } else {
        noteLines = works
          .map(w => translateNote(w.expert_note))
          .filter(Boolean)
          .filter((n, i, arr) => arr.indexOf(n) === i) // dédupliquer
          .filter(n => n.length > 5) // exclure les résidus vides après nettoyage
          .slice(0, 3);
      }

      if (noteLines.length > 0) {
        checkY(20);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...BLACK);
        doc.text(tr("export.sections.expertNote"), margin, y);
        y += 2;
        doc.setDrawColor(...GOLD);
        doc.line(margin, y + 1, margin + contentW, y + 1);
        y += 8;
        // eslint-disable-next-line no-loop-func
        noteLines.forEach(note => {
          checkY(12);
          const lines = doc.splitTextToSize(sanitize(note), contentW);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...GRAY);
          lines.slice(0, 5).forEach(l => { doc.text(l, margin, y); y += 5; });
          y += 2;
        });
      }

      // Pied de page Note artiste
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text(tr("export.confidentialShort"), margin, 285);

      // ── Fiches individuelles de cet artiste ─────────────────────────────
      for (const [, aw] of works.entries()) {
        doc.addPage();
        y = margin;

        // En-tête fiche
        doc.setFillColor(...BLACK);
        doc.rect(0, 0, W, 18, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const ficheNum = ficheArtworks.indexOf(aw) + 1;
        // Le symbole ° (U+00B0) est dans la plage latin-1, on l'ecrit directement hors sanitize
        doc.text(`FICHE N\xb0${ficheNum} - ${sanitize(aw.inventory_number || aw.id.substring(0, 8))}`, margin, 12);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(sanitize(`${today(locale)} - QUANG & CO`), W - margin - 50, 12);

        y = 28;

        // Titre + ligne descriptive + miniature principale compressée
        const thumb = await getThumbForArtwork(aw);
        const hasThumb = Boolean(thumb?.dataUrl);
        const thumbBox = hasThumb ? 58 : 0; // env. 5,8 cm : lisible mais léger
        const thumbX = W - margin - thumbBox;
        const thumbY = y;
        const textMaxW = hasThumb ? contentW - thumbBox - 10 : contentW;

        if (hasThumb) {
          const fit = imageFitBox(thumb.width, thumb.height, thumbBox, thumbBox);
          const imgX = thumbX + (thumbBox - fit.w) / 2;
          const imgY = thumbY + (thumbBox - fit.h) / 2;
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(220, 220, 220);
          doc.roundedRect(thumbX, thumbY, thumbBox, thumbBox, 1.5, 1.5, "FD");
          try {
            doc.addImage(thumb.dataUrl, "JPEG", imgX, imgY, fit.w, fit.h, undefined, "FAST");
          } catch (e) {
            // En cas de CORS/image invalide, on continue sans bloquer le rapport.
          }
        }

        doc.setTextColor(...BLACK);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        const titleLines = doc.splitTextToSize(sanitize(aw.title || tr("common.untitled")), textMaxW);
        // eslint-disable-next-line no-loop-func
        titleLines.slice(0, 3).forEach(line => {
          doc.text(line, margin, y);
          y += 6;
        });
        y += 2;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...GRAY);
        const descLines = doc.splitTextToSize(sanitize([
          aw.artist || "-",
          [aw.year, decodeMedium(aw, tr), aw.dimensions].filter(Boolean).join(" \u2014 "),
        ].filter(Boolean).join("  /  ")), textMaxW);
        // eslint-disable-next-line no-loop-func
        descLines.slice(0, 3).forEach(line => {
          doc.text(line, margin, y);
          y += 5;
        });

        if (hasThumb) y = Math.max(y + 6, thumbY + thumbBox + 8);
        else y += 12;

        // Valeur vénale encadrée — hauteur fixe 20mm, valeur + fourchette sur 2 lignes si trop long
        const estVal  = getEstVal(aw);
        const estLow  = getEstLow(aw);
        const estHigh = getEstHigh(aw);
        const valStr  = formatEur(estVal);
        // Fourchette : si elle tient sur la même ligne (< 80 chars restants) → inline, sinon ligne séparée
        const frcStr  = (estLow && estHigh) ? `Fourchette : ${formatEur(estLow)} - ${formatEur(estHigh)}` : null;
        // Mesure grossière : valStr long > 20 chars → fourchette sur ligne 3
        const valLong = valStr.length > 18;
        const boxH    = valLong ? 28 : 22;
        checkY(boxH + 4);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(...GOLD);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, y, contentW, boxH, 2, 2, "FD");
        doc.setTextColor(...BLACK);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.text(tr("export.sections.marketValue"), margin + 5, y + 7);
        doc.setFontSize(15);
        doc.setTextColor(150, 110, 50);
        doc.text(valStr, margin + 5, y + 16);
        if (frcStr) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(...GRAY);
          if (valLong) {
            // fourchette sur sa propre ligne
            doc.text(sanitize(frcStr), margin + 5, y + 24);
          } else {
            // fourchette inline à droite de la valeur — x dynamique depuis la valeur
            doc.text(sanitize(frcStr), margin + 5 + Math.min(valStr.length * 3.2 + 8, 80), y + 16);
          }
        }
        y += boxH + 8;

        // ── État de conservation ──────────────────────────────────────────────
        const condLbl = conditionLabel(aw.condition, tr);
        {
          const condText = condLbl
            ? `ETAT DE CONSERVATION : ${condLbl}`
            : tr("export.sections.unknownCondition");
          const isUnknown = !condLbl;
          doc.setFont("helvetica", isUnknown ? "bold" : "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(...(isUnknown ? [160, 80, 40] : GRAY));
          doc.text(sanitize(condText), margin, y);
          y += 8;
        }

        // Méthode lisible
        const mLabelFiche = methodLabel(aw.expert_method, tr);
        if (mLabelFiche) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.setTextColor(...GRAY);
          doc.text(sanitize(mLabelFiche), margin, y);
          y += 8;
        }

        const ownArtistKey = normalizeArtistKey(aw.artist);

        // Détecter le groupe pour adapter l'affichage
        const awMethodRaw = String(aw?.expert_method || "").toLowerCase();
        const awIsGroupA = awMethodRaw.includes("direct_cup") || awMethodRaw.includes("group_a") || awMethodRaw === "a";
        const awIsGroupC = awMethodRaw.includes("point") || awMethodRaw.includes("group_c") || awMethodRaw === "c";

        // Note d'expert narrative — ventes retenues et calcul
        const selectedRowsForWork = selectedComparablesParArtiste[ownArtistKey] || [];
        const fallbackRowsForWork = (comparablesParArtiste[ownArtistKey] || []).slice(0, 5);
        const aqForWork = artistQuotesParArtiste[ownArtistKey] || [];
        const expertNarrative = buildExpertNarrative(aw, selectedRowsForWork, fallbackRowsForWork, aqForWork, tr);
        if (expertNarrative) {
          checkY(24);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLACK);
          doc.text(tr("export.sections.expertNote"), margin, y);
          y += 5;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...GRAY);
          const narrativeLines = doc.splitTextToSize(sanitize(expertNarrative), contentW);
          // eslint-disable-next-line no-loop-func
          narrativeLines.slice(0, 14).forEach(l => { doc.text(l, margin, y); y += 5; });
          y += 3;
        }

        // ── Signature & Authenticité ────────────────────────────────────────
        {
          const sigInfo = (() => {
            try {
              const raw = aw.signature_info;
              if (!raw) return {};
              return typeof raw === "string" ? JSON.parse(raw) : raw;
            } catch { return {}; }
          })();

          const isSigned = aw.signature === true
            || sigInfo.signed === true
            || sigInfo.signed === "yes"
            || sigInfo.signed === "oui";
          const isUnsigned = aw.signature === false
            || sigInfo.signed === false
            || sigInfo.signed === "no"
            || sigInfo.signed === "non";
          const signedLabel = isSigned ? tr("common.yes") : isUnsigned ? tr("common.no") : tr("common.unknown");

          const sigLocation = sigInfo.location
            || sigInfo.signature_location
            || sigInfo.signature_details
            || aw.signature_desc
            || null;

          const hasCert = Boolean(aw.certificate_auth);
          const certText = aw.certificate_authenticity || null;
          const certLabel = hasCert
            ? (certText ? `${tr("export.present")} (${sanitize(certText.substring(0, 60))})` : tr("export.present"))
            : certText
              ? `${tr("export.absent")} - ${sanitize(certText.substring(0, 60))}`
              : tr("export.absent");

          const lines = [
            `Signature : ${signedLabel}${sigLocation ? "  —  " + sanitize(sigLocation) : ""}`,
            `Certificat d'authenticite : ${certLabel}`,
          ];

          const blockH = 6 + lines.length * 5 + 4;
          checkY(blockH);
          y += 2;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLACK);
          doc.text(tr("export.sections.signature"), margin, y);
          y += 5;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...GRAY);
          // eslint-disable-next-line no-loop-func
          lines.forEach(l => {
            doc.text(l, margin, y);
            y += 5;
          });
          y += 3;
        }

        // Artistes analogues — Groupe B uniquement
        const cupObj = getCupObj(aw);
        const analogArtistsRaw = cupObj?.analog_artists || aqForWork.flatMap(q => Array.isArray(q.analog_artists) ? q.analog_artists : []);
        if (!awIsGroupA && !awIsGroupC && analogArtistsRaw.length > 0) {
          checkY(14);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLACK);
          doc.text(tr("export.sections.analogArtists"), margin, y);
          y += 5;
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.setTextColor(...GRAY);
          doc.text(sanitize([...new Set(analogArtistsRaw)].join(", ")), margin + 3, y);
          y += 9;
        }

        // Ventes comparables — Groupe A et B uniquement
        // Exclus : Groupe C (points), personal_quote*, direct_cup_segmented
        // Pour ces methodes la fourchette vient de artist_quotes, pas de ventes individuelles.
        const awMethodRawFiche = String(aw?.expert_method || "").toLowerCase();
        const awIsPersonalQuote = awMethodRawFiche.includes("personal_quote") || awMethodRawFiche.includes("direct_cup_segmented");
        const cupCompRaw = Array.isArray(aw.cup_comparables) ? aw.cup_comparables : [];
        const selectedCompsForWork = (selectedComparablesParArtiste[ownArtistKey] || []).slice(0, 5);
        const dbComps = (comparablesParArtiste[ownArtistKey] || []).slice(0, 5);
        const allComps = (awIsGroupC || awIsPersonalQuote)
          ? []
          : (selectedCompsForWork.length > 0 ? selectedCompsForWork : (dbComps.length > 0 ? dbComps : cupCompRaw));

        if (allComps.length > 0) {
          checkY(20);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(...BLACK);
          doc.text(tr("export.sections.reference"), margin, y);
          y += 2;
          doc.setDrawColor(...GOLD);
          doc.line(margin, y + 1, margin + contentW, y + 1);
          y += 8;

          // Version compacte : le détail complet des comparables reste dans la base,
          // le rapport principal affiche seulement les références lisibles sans manger 2 pages.
          const compactComps = allComps.slice(0, 5);
          const rowH = 8;
          checkY(8 + compactComps.length * rowH + 6);

          doc.setFillColor(248, 246, 242);
          doc.rect(margin, y, contentW, 7, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(6.8);
          doc.setTextColor(...GRAY);
          doc.text(tr("export.table.artwork").toUpperCase(), margin + 2, y + 5);
          doc.text(tr("export.table.auctionDate"), margin + 78, y + 5);
          doc.text(tr("export.table.dimensions").toUpperCase(), margin + 122, y + 5);
          doc.text(tr("export.table.price"), W - margin - 2, y + 5, { align: "right" });
          y += 7;

          // eslint-disable-next-line no-loop-func
          compactComps.forEach(c => {
            const cTitle    = c.title || c.titre || c.artwork_title || tr("common.untitled");
            const cArtist   = c.artist || aw.artist || "";
            const cTech     = c.technique || c.medium || decodeMedium(c, tr) || "";
            const cDims     = c.dimensions_cm || c.dimensions || "";
            const cHammer   = parseFloat(c.hammer_price || c.prix_adjuge || c.price || 0) || 0;
            const cEst      = parseFloat(c.estimated_value || 0) || 0;
            const cMaison   = extractMaison(c);
            const rawDateComp = c.sale_date || c.auction_date || c.date || null;
            const cDate     = rawDateComp ? fmtDate(rawDateComp, locale) : "";
            const isAnalogue = cArtist && cArtist.toLowerCase() !== (aw.artist || "").toLowerCase();

            const fmtFiche = (v) => String(Math.round(Math.abs(Number(v)))).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " EUR";
            const prixStr = cHammer ? fmtFiche(cHammer) : cEst ? `Est. ${fmtFiche(cEst)}` : "—";
            const titleStr = isAnalogue
              ? `${cArtist} — ${cTitle}`
              : cTitle;
            const maisonDateStr = [cMaison, cDate].filter(Boolean).join(" · ");
            const dimStr = cDims || cTech;

            checkY(rowH + 2);
            doc.setDrawColor(230, 226, 218);
            doc.line(margin, y, margin + contentW, y);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.2);
            doc.setTextColor(...BLACK);
            doc.text(sanitize(titleStr.substring(0, 44)), margin + 2, y + 5);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(6.6);
            doc.setTextColor(...GRAY);
            doc.text(sanitize(maisonDateStr.substring(0, 34)), margin + 78, y + 5);
            doc.text(sanitize(String(dimStr).substring(0, 18)), margin + 122, y + 5);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.2);
            doc.setTextColor(150, 110, 50);
            doc.text(prixStr, W - margin - 2, y + 5, { align: "right" });
            y += rowH;
          });

          if (allComps.length > compactComps.length) {
            doc.setFont("helvetica", "italic");
            doc.setFontSize(6.5);
            doc.setTextColor(...GRAY);
            doc.text(sanitize(`+ ${allComps.length - compactComps.length} référence(s) complémentaire(s) conservée(s) en mémoire expert.`), margin + 2, y + 4);
            y += 6;
          }
          y += 2;
        }

        const docSummary = buildDocumentSummary(aw, tr);
        if (docSummary) {
          checkY(14);
          y += 3;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLACK);
          doc.text(tr("export.sections.documentation"), margin, y);
          y += 5;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...GRAY);
          doc.text(sanitize(docSummary.substring(0, 110)), margin, y);
          y += 6;
        }

        // COMMENTAIRE COMPLÉMENTAIRE supprimé — fusionné dans NOTE DE L'EXPERT

        // ── Localisation et conditions de stockage ──────────────────────────
        const locName   = aw.locations?.name || aw.location_name || null;
        const locDetail = aw.storage_location_detail || null;
        const locRisk   = storageRiskLabel(aw.storage_risk, tr);
        const locVisit  = aw.visited_at ? fmtDate(aw.visited_at, locale) : null;

        if (locName || locDetail || locRisk) {
          checkY(20);
          y += 3;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLACK);
          doc.text(tr("export.sections.storage"), margin, y);
          y += 5;

          doc.setFillColor(248, 246, 242);
          doc.roundedRect(margin, y, contentW, locDetail ? 18 : 12, 1.5, 1.5, "F");

          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...BLACK);
          const locLine1Parts = [locName, locRisk ? `[${locRisk}]` : null].filter(Boolean);
          doc.text(sanitize(locLine1Parts.join("   ")), margin + 4, y + 7);

          if (locDetail) {
            doc.setFont("helvetica", "italic");
            doc.setFontSize(7.5);
            doc.setTextColor(...GRAY);
            doc.text(sanitize(locDetail.substring(0, 90)), margin + 4, y + 14);
          }

          if (locVisit) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...GRAY);
            doc.text(sanitize(`Visite : ${locVisit}`), W - margin - 2, y + 7, { align: "right" });
          }

          y += (locDetail ? 22 : 16);
        }

        // Pied de page
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...GRAY);
        doc.text(
          doc.splitTextToSize(`${valuationFooter(artworks, String(locale).slice(0, 2))} ${tr("export.disclaimer")}`, 210 - 2 * margin),
          margin, 280
        );
      }
    }
  }


  // ── Pages finales : statuts particuliers + œuvres non estimées ───────────

  const LOSS_LABELS = {
    vol: tr("export.lossTypes.vol"), perte: tr("export.lossTypes.loss"), destruction: tr("export.lossTypes.destruction"),
    incendie: tr("export.lossTypes.fire"), inondation: tr("export.lossTypes.flood"), autre: tr("export.lossTypes.other"),
  };

    // Détecte une œuvre classée write-off (irréparable, valeur 0 délibérée)
  function isWriteOff(a) {
    if (a.valuation_method === "condition_write_off") return true;
    const obj = getCupObj(a);
    if (obj?.method_group === "write-off") return true;
    if (String(a.expert_method || "").toLowerCase().includes("write")) return true;
    return false;
  }

  // Catégoriser les œuvres sans valeur estimée
  const writeOffs     = artworks.filter(a => isWriteOff(a));
  const vendues       = artworks.filter(a => !getEstVal(a) && !isWriteOff(a) && a.artwork_status === "sold");
  const perduesVolees = artworks.filter(a => !getEstVal(a) && !isWriteOff(a) && a.loss_type);
  const nonEstimees   = artworks.filter(a =>
    !getEstVal(a) &&
    !isWriteOff(a) &&
    a.artwork_status !== "sold" &&
    !a.loss_type
  );

  // ── Section 0 : Œuvres irréparables — Write-off ─────────────────────────────
  if (writeOffs.length > 0) {
    doc.addPage();
    y = margin;
    // En-tête bordeaux foncé
    doc.setFillColor(90, 20, 20);
    doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(tr("export.sections.writeoff"), margin, 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(tr("export.writeoffSummary", { count: writeOffs.length }), margin, 18);
    y = 30;

    // Intro explicative
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(90, 20, 20);
    const introLines = doc.splitTextToSize(
      tr("export.writeoffIntro"),
      contentW
    );
    introLines.forEach(l => { checkY(5); doc.text(l, margin, y); y += 4.5; });
    y += 6;

    // Tableau récap
    // Colonnes adaptées à contentW=170mm : Inventaire|Artiste|Titre|Technique|Dim|Val.théo|État
    // Total : 20+28+44+28+22+22+26 = 190 → on clip à contentW
    // Vérifié : 20+28+44+28+22+22+26 = 190 → on réduit Titre à 38 et Technique à 24
    // 20+28+38+24+22+22+26 = 180 → encore trop, ajuster :
    // 18+26+38+24+20+22+22 = 170 ✓
    const colWFinal = [18, 26, 38, 24, 20, 22, 22];
    const colX = [margin];
    colWFinal.forEach((w, i) => { if (i < colWFinal.length - 1) colX.push(colX[i] + colWFinal[i]); });
    const headers = [tr("export.table.inventory"), tr("export.table.artist"), tr("export.table.titleYear"), tr("export.table.medium"), tr("export.table.dimensions"), tr("export.table.theoretical"), tr("export.table.condition")];

    // En-tête tableau
    checkY(10);
    doc.setFillColor(90, 20, 20);
    doc.rect(margin, y, contentW, 9, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    headers.forEach((h, i) => doc.text(h, colX[i] + 1, y + 6));
    y += 9;

    writeOffs.forEach((a, idx) => {
      checkY(9);
      // Fond alterné
      if (idx % 2 === 0) {
        doc.setFillColor(252, 240, 240);
        doc.rect(margin, y, contentW, 8, "F");
      }

      // Valeur théorique : extraire du expert_note si possible
      let valTheo = "—";
      const noteRaw = a.expert_note || "";
      const matchTheo = noteRaw.match(/(\d[\d\s]*[\d])\s*[–-]\s*(\d[\d\s]*[\d])\s*€/);
      if (matchTheo) {
        valTheo = `${matchTheo[1].trim()} - ${matchTheo[2].trim()} EUR`;
      }

      const inv   = a.inventory_number || "—";
      const artist = sanitize((a.artist || tr("common.unattributed")).substring(0, 15));
      const titre  = sanitize(((a.title || tr("common.untitled")) + (a.year ? ` (${a.year})` : "")).substring(0, 26));
      const tech   = sanitize(decodeMedium(a, tr).substring(0, 18));
      const dims   = sanitize((a.dimensions || "—").substring(0, 12));
      const etat   = sanitize(a.conditionNotes ? a.conditionNotes.substring(0, 16) : (a.condition || tr("export.irreparable")));

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...BLACK);
      doc.text(sanitize(inv),   colX[0] + 1, y + 5.5);
      doc.text(artist,          colX[1] + 1, y + 5.5);
      doc.text(titre,           colX[2] + 1, y + 5.5);
      doc.text(tech,            colX[3] + 1, y + 5.5);
      doc.text(dims,            colX[4] + 1, y + 5.5);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(90, 20, 20);
      doc.text(valTheo,         colX[5] + 1, y + 5.5);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(6.5);
      doc.setTextColor(100, 60, 60);
      doc.text(sanitize(etat.substring(0, 22)), colX[6] + 1, y + 5.5);
      y += 8;
    });

    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(
      tr("export.theoreticalDisclaimer"),
      margin, 285
    );
  }

  // ── Section 1 : Pertes / Vols / Destructions (valeur 0) ──────────────────
  if (perduesVolees.length > 0) {
    doc.addPage();
    y = margin;
    doc.setFillColor(120, 30, 30);
    doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(tr("export.sections.losses"), margin, 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${perduesVolees.length} oeuvre(s)  |  Valeur residuelle : 0 EUR`, margin, 18);
    y = 30;

    const byArtistPV = {};
    perduesVolees.forEach(a => {
      const k = a.artist || tr("common.unattributed");
      if (!byArtistPV[k]) byArtistPV[k] = [];
      byArtistPV[k].push(a);
    });

    Object.entries(byArtistPV)
      .sort(([a], [b]) => artistSortKey(a).localeCompare(artistSortKey(b), locale, { sensitivity: "base" }))
      .forEach(([artist, works]) => {
      checkY(14);
      doc.setFillColor(...LGRAY);
      doc.rect(margin, y, contentW, 10, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...BLACK);
      doc.text(sanitize(artist), margin + 3, y + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 30, 30);
      doc.text(tr("export.zeroValueCount", { count: works.length }), W - margin - 40, y + 7);
      y += 13;

      sortByInventory(works).forEach(w => {
        checkY(8);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...BLACK);
        const inv = w.inventory_number ? `[${w.inventory_number}]` : "";
        const lossLabel = (w.loss_type === "irreparable")
          ? tr("export.irreparable")
          : (LOSS_LABELS[w.loss_type] || w.loss_type || tr("export.event"));
        doc.text(
          sanitize(`${inv}  ${w.title || tr("common.untitled")}  -  ${[w.year, decodeMedium(w, tr)].filter(Boolean).join(", ")}`),
          margin + 5, y
        );
        doc.setFont("helvetica", "italic");
        doc.setFontSize(7);
        doc.setTextColor(120, 30, 30);
        doc.text(sanitize(`Statut : ${lossLabel} — valeur retenue : 0 EUR`), margin + 5, y + 4.5);
        doc.setTextColor(...BLACK);
        y += 11;
      });
      y += 3;
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(tr("export.lossDisclaimer"), margin, 285);
  }

  // ── Section 2 : Œuvres vendues / cédées ──────────────────────────────────
  if (vendues.length > 0) {
    doc.addPage();
    y = margin;
    doc.setFillColor(30, 60, 30);
    doc.rect(0, 0, W, 18, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(tr("export.sections.sold"), margin, 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${vendues.length} oeuvre(s) non valorisees`, W - margin - 60, 12);
    y = 28;

    const byArtistV = {};
    vendues.forEach(a => {
      const k = a.artist || tr("common.unattributed");
      if (!byArtistV[k]) byArtistV[k] = [];
      byArtistV[k].push(a);
    });

    Object.entries(byArtistV)
      .sort(([a], [b]) => artistSortKey(a).localeCompare(artistSortKey(b), locale, { sensitivity: "base" }))
      .forEach(([artist, works]) => {
      checkY(14);
      doc.setFillColor(...LGRAY);
      doc.rect(margin, y, contentW, 10, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...BLACK);
      doc.text(sanitize(artist), margin + 3, y + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(30, 80, 30);
      doc.text(`${works.length} oeuvre(s)`, W - margin - 25, y + 7);
      y += 13;

      sortByInventory(works).forEach(w => {
        checkY(7);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...BLACK);
        const inv = w.inventory_number ? `[${w.inventory_number}]` : "";
        doc.text(
          sanitize(`${inv}  ${w.title || tr("common.untitled")}  -  ${[w.year, decodeMedium(w, tr)].filter(Boolean).join(", ")}`),
          margin + 5, y
        );
        y += 6;
      });
      y += 3;
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(tr("export.soldDisclaimer"), margin, 285);
  }

  // ── Section 3 : Œuvres non encore estimées ───────────────────────────────
  if (nonEstimees.length > 0) {
    doc.addPage();
    y = margin;
    doc.setFillColor(...BLACK);
    doc.rect(0, 0, W, 18, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(tr("export.sections.pending"), margin, 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${nonEstimees.length} oeuvre(s) sans valorisation`, W - margin - 60, 12);
    y = 28;

    const byArtistNE = {};
    nonEstimees.forEach(a => {
      const k = a.artist || tr("common.unattributed");
      if (!byArtistNE[k]) byArtistNE[k] = [];
      byArtistNE[k].push(a);
    });

    Object.entries(byArtistNE)
      .sort(([a], [b]) => artistSortKey(a).localeCompare(artistSortKey(b), locale, { sensitivity: "base" }))
      .forEach(([artist, works]) => {
      checkY(14);
      doc.setFillColor(...LGRAY);
      doc.rect(margin, y, contentW, 10, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...BLACK);
      doc.text(sanitize(artist), margin + 3, y + 7);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...GRAY);
      doc.text(`${works.length} oeuvre(s)`, W - margin - 25, y + 7);
      y += 13;

      sortByInventory(works).forEach(w => {
        checkY(8);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...BLACK);
        const inv = w.inventory_number ? `[${w.inventory_number}]` : "";
        doc.text(sanitize(`${inv}  ${w.title || tr("common.untitled")}  -  ${[w.year, decodeMedium(w, tr)].filter(Boolean).join(", ")}`), margin + 5, y);
        y += 6;
      });
      y += 3;
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(tr("export.pendingDisclaimer"), margin, 285);
  }

  // Sauvegarde
  const slug = collectionTitle.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
  doc.save(`${tr("export.filePrefix")}_${slug}.pdf`);
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function ExpertReportGenerator() {
  const { user } = useAuth() || {};
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  const tr = useCallback((key, options = {}) => t(`expertReport.${key}`, options), [t]);
  const [searchParams] = useSearchParams();
  const urlCollectionId   = searchParams.get("collectionId");
  const urlCollectionName = searchParams.get("collectionName");
  const urlClientName     = searchParams.get("clientName");

  const [collections,           setCollections]           = useState([]);
  const [selectedCollection,    setSelectedCollection]    = useState(urlCollectionId || "");
  const [collectionTitle,       setCollectionTitle]       = useState(urlCollectionName ? decodeURIComponent(urlCollectionName) : "");
  const [artworks,              setArtworks]              = useState([]);
  const [comparablesParArtiste, setComparablesParArtiste] = useState({}); // { "artist_key" → [{...}] }
  const [selectedComparablesParArtiste, setSelectedComparablesParArtiste] = useState({}); // ventes réellement retenues par l'expert
  const [artistQuotesParArtiste, setArtistQuotesParArtiste] = useState({}); // { "artist_key" → [artist_quotes row, ...] }
  const [,                       setLotTitlesMap]           = useState({}); // { lot_id → title }
  const [clientName,            setClientName]            = useState(urlClientName ? decodeURIComponent(urlClientName) : "");
  const [loadingArtworks,       setLoadingArtworks]       = useState(false);
  const [generating,            setGenerating]            = useState(null); // "pdf" | "word" | "excel" | null
  const [reportMode,            setReportMode]            = useState("cup_60"); // "synthese" | "cup_60" | "cup_200"
  const [outputFormats,         setOutputFormats]         = useState({ pdf: true, word: false }); // formats à générer

  // Charger collections — propres + partagées via collection_permissions
  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      // Collections propres
      supabase.from("collections").select("id, title").eq("client_id", user.id),
      // Collections partagées (expert/advisor)
      supabase.from("collection_permissions")
        .select("collection_id, collections:collection_id(id, title)")
        .eq("grantee_id", user.id)
        .eq("can_view", true)
        .not("collection_id", "is", null),
    ]).then(([own, shared]) => {
      const ownList = own.data || [];
      const sharedList = (shared.data || [])
        .map(p => p.collections)
        .filter(Boolean);
      // Dédupliquer par id
      const all = [...ownList, ...sharedList];
      const unique = all.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
      setCollections(unique);
    });
  }, [user?.id]);

  // Charger œuvres quand collection sélectionnée
  useEffect(() => {
    if (!selectedCollection) { setArtworks([]); setComparablesParArtiste({}); setSelectedComparablesParArtiste({}); return; }
    setLoadingArtworks(true);
    const processArtworks = (rows) => {
      if (!rows?.length) return rows || [];
      // Propager la valeur depuis l'œuvre source de lot vers les exemplaires.
      // EstimationSemblablesPage stocke la valeur uniquement sur l'œuvre source ;
      // les autres exemplaires du même lot ont cup_comparables null.
      // On construit un index { lot_id → valeur source } depuis les œuvres qui ont une valeur.
      const lotValMap = {};
      rows.forEach(aw => {
        if (aw.lot_id) {
          const v = getEstVal(aw);
          if (v && !lotValMap[aw.lot_id]) {
            lotValMap[aw.lot_id] = {
              valuation_estimated_value: aw.valuation_estimated_value || v,
              valuation_low:  aw.valuation_low  || Math.round(v * 0.9),
              valuation_high: aw.valuation_high || Math.round(v * 1.1),
              expert_method:  aw.expert_method,
              expert_date:    aw.expert_date,
            };
          }
        }
      });
      // Appliquer aux exemplaires sans valeur
      return rows.map(aw => {
        if (aw.lot_id && !getEstVal(aw) && lotValMap[aw.lot_id]) {
          return { ...aw, ...lotValMap[aw.lot_id] };
        }
        return aw;
      });
    };

    // RPC SECURITY DEFINER — contourne la limite PostgREST et la RLS
    supabase
      .rpc("get_expert_collection_artworks", { p_collection_id: selectedCollection })
      .then(({ data, error }) => {
        if (error) console.error("get_expert_collection_artworks error:", error.message);
        const rows = processArtworks(data || []);
        setArtworks(rows);
        setLoadingArtworks(false);

        // Les RPC / vues sécurisées peuvent parfois omettre image_url/images.
        // Pour le PDF, on recharge uniquement les champs image réellement présents
        // dans public.artworks, en lots courts pour éviter les URLs PostgREST géantes.
        // Ne PAS demander main_image_url / cover_image_url / thumbnail_url ici :
        // ces colonnes ne sont pas garanties et peuvent provoquer un 400/404 PostgREST.
        const uniqueArtworkIdsForImages = [...new Set(rows.map(r => r.id).filter(Boolean))];

        const loadArtworkImages = async () => {
          if (uniqueArtworkIdsForImages.length === 0) return;

          const chunks = [];
          for (let i = 0; i < uniqueArtworkIdsForImages.length; i += 80) {
            chunks.push(uniqueArtworkIdsForImages.slice(i, i + 80));
          }

          const imageRows = [];
          for (const chunk of chunks) {
            const { data, error } = await supabase
              .from("artworks")
              .select("id, image_url, images, primary_image_path")
              .in("id", chunk);

            if (error) {
              console.warn("Chargement images principales PDF :", error.message);
              continue;
            }

            imageRows.push(...(data || []));
          }

          if (!imageRows.length) return;

          const imageById = Object.fromEntries(imageRows.map(r => [r.id, r]));
          setArtworks(prev => prev.map(aw => {
            const img = imageById[aw.id];
            if (!img) return aw;
            return {
              ...aw,
              image_url: aw.image_url || img.image_url,
              images: aw.images || img.images,
              primary_image_path: aw.primary_image_path || img.primary_image_path,
            };
          }));
        };

        loadArtworkImages();

        // La RPC / vue sécurisée peut omettre signature/signature_info/signature_desc.
        // On recharge ces champs directement depuis public.artworks pour garantir leur présence.
        const loadArtworkSignatures = async () => {
          const ids = [...new Set(rows.map(r => r.id).filter(Boolean))];
          if (ids.length === 0) return;
          const chunks = [];
          for (let i = 0; i < ids.length; i += 80) chunks.push(ids.slice(i, i + 80));
          const sigRows = [];
          for (const chunk of chunks) {
            const { data, error } = await supabase
              .from("artworks")
              .select("id, signature, signature_desc, signature_info, certificate_auth, certificate_authenticity")
              .in("id", chunk);
            if (error) { console.warn("Chargement signatures PDF :", error.message); continue; }
            sigRows.push(...(data || []));
          }
          if (!sigRows.length) return;
          const sigById = Object.fromEntries(sigRows.map(r => [r.id, r]));
          setArtworks(prev => prev.map(aw => {
            const s = sigById[aw.id];
            if (!s) return aw;
            return {
              ...aw,
              signature:                aw.signature                ?? s.signature,
              signature_desc:           aw.signature_desc           ?? s.signature_desc,
              signature_info:           aw.signature_info           ?? s.signature_info,
              certificate_auth:         aw.certificate_auth         ?? s.certificate_auth,
              certificate_authenticity: aw.certificate_authenticity ?? s.certificate_authenticity,
            };
          }));
        };

        loadArtworkSignatures();

        const col = collections.find(c => c.id === selectedCollection);
        if (col) setCollectionTitle(col.title);

        // Charger les titres de lots pour les afficher dans les fiches
        const distinctLotIds = [...new Set(rows.map(r => r.lot_id).filter(Boolean))];
        if (distinctLotIds.length > 0) {
          supabase
            .from("lots")
            .select("id, title")
            .in("id", distinctLotIds)
            .then(({ data: lotRows }) => {
              const map = Object.fromEntries((lotRows || []).map(l => [l.id, l.title]));
              setLotTitlesMap(map);
              // Ré-injecter lot_title dans les artworks déjà chargés
              setArtworks(prev => prev.map(aw =>
                aw.lot_id && map[aw.lot_id] ? { ...aw, lot_title: map[aw.lot_id] } : aw
              ));
            });
        }

        // Charger les documents liés aux fiches ET aux lots.
        // Important : les documents communs au lot (facture, rapport de conservation, etc.)
        // ne sont pas forcément copiés dans artwork.documents ; on les rattache donc
        // virtuellement à chaque œuvre du lot pour le calcul documentaire du rapport.
        const artworkIdsForDocs = rows.map(r => r.id).filter(Boolean);
        const lotIdsForDocs = [...new Set(rows.map(r => r.lot_id).filter(Boolean))];

        const mergeDocumentRows = (docRows = []) => {
          setArtworks(prev => prev.map(aw => {
            const ownDocs = docRows.filter(d => d.artwork_id === aw.id);
            const lotDocs = aw.lot_id ? docRows.filter(d => d.lot_id === aw.lot_id) : [];
            const mergedDocs = [...ownDocs, ...lotDocs].filter((d, i, arr) =>
              arr.findIndex(x => (x.id && x.id === d.id) || JSON.stringify(x) === JSON.stringify(d)) === i
            );
            const flags = inferDocumentFlagsFromRows(mergedDocs, aw);
            return {
              ...aw,
              _document_rows: mergedDocs,
              has_invoice: flags.has_invoice,
              has_certificate: flags.has_certificate,
              has_condition_report: flags.has_condition_report,
            };
          }));
        };

        Promise.all([
          artworkIdsForDocs.length
            ? supabase.from("documents").select("*").in("artwork_id", artworkIdsForDocs)
            : Promise.resolve({ data: [] }),
          lotIdsForDocs.length
            ? supabase.from("documents").select("*").in("lot_id", lotIdsForDocs)
            : Promise.resolve({ data: [] }),
        ]).then(([byArtwork, byLot]) => {
          const docRows = [...(byArtwork.data || []), ...(byLot.data || [])];
          mergeDocumentRows(docRows);
        }).catch(err => {
          console.warn("Chargement documents œuvres/lots :", err?.message || err);
        });

        // Charger les comparables de marché depuis estimation_comparables
        // groupés par artist_key pour alimenter les pages "Note de marché" et les fiches
        const artistKeys = [...new Set(rows.map(r => {
          return (r.artist || "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]+/g, " ")
            .trim();
        }).filter(Boolean))];
        if (artistKeys.length === 0) return;
        supabase
          .from("estimation_comparables")
          .select("artist_key, title, artist, medium, dimensions, auction_house, auction_date, estimated_value, hammer_price, source_type, comment, source")
          .in("artist_key", artistKeys)
          .then(({ data: compData }) => {
            // Tri côté JS par auction_date desc (évite le 400 PostgREST sur order)
            const sorted = (compData || []).slice().sort((a, b) => {
              const da = a.auction_date ? new Date(a.auction_date).getTime() : 0;
              const db = b.auction_date ? new Date(b.auction_date).getTime() : 0;
              return db - da;
            });
            const grouped = {};
            sorted.forEach(c => {
              const k = c.artist_key || normalizeArtistKey(c.artist);
              if (!grouped[k]) grouped[k] = [];
              grouped[k].push(c);
            });
            setComparablesParArtiste(grouped);
          });

        // Charger les cotations expertales depuis artist_quotes (méthode, fourchette, analogies)
        supabase
          .from("artist_quotes")
          .select("artist_key, medium_key, medium, price_low, price_high, average_value, method_group, analog_artists, analogy_reason, notes, confidence, quote_date")
          .in("artist_key", artistKeys)
          .then(({ data: quotesData }) => {
            const groupedQ = {};
            (quotesData || []).forEach(q => {
              const k = q.artist_key;
              if (!k) return;
              if (!groupedQ[k]) groupedQ[k] = [];
              groupedQ[k].push(q);
            });
            setArtistQuotesParArtiste(groupedQ);
          });

        // Charger la mémoire expert réelle : estimation_selected_comparables.
        // C'est cette table qui contient les ventes effectivement retenues pour justifier le rapport.
        const artworkIds = rows.map(r => r.id).filter(Boolean);
        if (artworkIds.length > 0) {
          supabase
            .from("estimations")
            .select("id, artwork_id")
            .in("artwork_id", artworkIds)
            .then(({ data: estimationRows, error: estimationError }) => {
              if (estimationError || !estimationRows?.length) {
                if (estimationError) console.warn("estimations report lookup:", estimationError.message);
                setSelectedComparablesParArtiste({});
                return;
              }

              const artworkById = Object.fromEntries(rows.map(r => [r.id, r]));
              const estimationToArtwork = Object.fromEntries(
                estimationRows.map(e => [e.id, artworkById[e.artwork_id]]).filter(([_, aw]) => !!aw)
              );
              const estimationIds = estimationRows.map(e => e.id).filter(Boolean);

              supabase
                .from("estimation_selected_comparables")
                .select(`
                  id, estimation_id, comparable_id,
                  kind, selected_price, weight, note, created_at,
                  artist_snapshot, title_snapshot, source_snapshot,
                  comparable:comparable_id (
                    auction_house, auction_date, hammer_price,
                    title, artist, medium, dimensions, comment, source, source_type
                  )
                `)
                .in("estimation_id", estimationIds)
                .order("created_at", { ascending: false })
                .then(({ data: selectedRows, error: selectedError }) => {
                  if (selectedError) {
                    console.warn("selected comparables report lookup:", selectedError.message);
                    setSelectedComparablesParArtiste({});
                    return;
                  }

                  const groupedSelected = {};
                  (selectedRows || []).forEach(row => {
                    const aw = estimationToArtwork[row.estimation_id];
                    const fallbackArtist = aw?.artist || row.artist_snapshot || "";
                    const k = normalizeArtistKey(fallbackArtist || row.artist_snapshot);
                    if (!k) return;
                    if (!groupedSelected[k]) groupedSelected[k] = [];
                    groupedSelected[k].push(selectedComparableToReportRow(row, fallbackArtist, tr("common.untitled")));
                  });

                  Object.keys(groupedSelected).forEach(k => {
                    groupedSelected[k] = dedupeReportComparables(groupedSelected[k]);
                  });
                  setSelectedComparablesParArtiste(groupedSelected);
                });
            });
        }
      });
  }, [selectedCollection, collections, tr]);

  // Statistiques
  const stats = useMemo(() => {
    const total      = artworks.length;
    const valued     = artworks.filter(a => getEstVal(a)).length;
    const cupDone    = artworks.filter(isCupComplete).length;
    const totalValue = artworks.reduce((s, a) => s + (getEstVal(a) || 0), 0);

    return { total, valued, cupDone, totalValue };
  }, [artworks]);

  // Option tarifaire détectée
  const detectedOption = useMemo(() => {
    if (stats.cupDone === 0 && stats.valued > 0) return { label: tr("options.synthese.label"), prix: tr("options.synthese.price"), mode: "synthese" };
    if (stats.cupDone <= 60) return { label: tr("options.cup_60.label"), prix: tr("options.cup_60.price"), mode: "cup_60" };
    return { label: tr("options.cup_200.label"), prix: tr("options.cup_200.price"), mode: "cup_200" };
  }, [stats, tr]);

  const handleGenerateReport = async () => {
    if (!outputFormats.pdf && !outputFormats.word) {
      alert(tr("alerts.selectFormat"));
      return;
    }
    setGenerating("report");
    try {
      if (outputFormats.pdf)  await generatePdf(artworks, collectionTitle, clientName, reportMode, comparablesParArtiste, selectedComparablesParArtiste, artistQuotesParArtiste, tr, locale);
      if (outputFormats.word) await generateWord(artworks, collectionTitle, clientName, reportMode, comparablesParArtiste, selectedComparablesParArtiste, artistQuotesParArtiste, tr, locale);
    } catch (e) { alert(tr("alerts.generation", { message: e.message })); }
    finally { setGenerating(null); }
  };

  const handleGenerateExcel = async () => {
    setGenerating("excel");
    try { await generateExcel(artworks, collectionTitle, tr, locale); }
    catch (e) { alert(tr("alerts.excel", { message: e.message })); }
    finally { setGenerating(null); }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-black mb-6 transition">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {tr("back")}
          </Link>
          <h1 className="text-3xl font-serif mb-1">{tr("title")}</h1>
          <p className="text-gray-500 text-sm">
            {tr("subtitle")}
          </p>
        </div>

        {/* Sélection collection */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{tr("collection")}</label>
              <select
                value={selectedCollection}
                onChange={e => setSelectedCollection(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              >
                <option value="">{tr("select")}</option>
                {collections.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{tr("clientName")}</label>
              <input
                type="text"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder={tr("clientPlaceholder")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              />
            </div>
          </div>

          {/* Stats */}
          {loadingArtworks && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> {tr("loading")}
            </div>
          )}

          {!loadingArtworks && artworks.length > 0 && (
            <>
              {/* Tableau de bord */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: tr("stats.total"), value: stats.total, icon: FileText },
                  { label: tr("stats.valued"), value: stats.valued, icon: BarChart2 },
                  { label: tr("stats.cupComplete"), value: `${stats.cupDone}/${stats.total}`, icon: CheckCircle2 },
                  { label: tr("stats.totalValue"), value: formatEur(stats.totalValue), icon: FileSpreadsheet },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="border border-gray-200 rounded-xl p-4">
                    <Icon className="w-4 h-4 text-gray-400 mb-2" />
                    <div className="text-xl font-serif text-gray-900">{value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>

              {/* Option tarifaire détectée */}
              <div className="p-4 border border-[#c9a961] rounded-xl bg-[#fffbf2] flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{tr("detected.title")} <strong>{detectedOption.label}</strong></p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {tr("detected.basedOn", { count: stats.cupDone })}
                  </p>
                </div>
                <span className="text-lg font-serif text-[#c9a961]">{detectedOption.prix}</span>
              </div>

              {/* Fiches incomplètes */}
              {stats.cupDone < stats.total && (
                <div className="flex items-start gap-3 p-4 border border-amber-200 rounded-xl bg-amber-50">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>{tr("incomplete", { count: stats.total - stats.cupDone })}</strong>
                  </div>
                </div>
              )}

              {/* Mode rapport */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tr("reportMode")}</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: "synthese", label: tr("options.synthese.short"), sub: tr("options.synthese.description"), prix: tr("options.synthese.price") },
                    { value: "cup_60", label: tr("options.cup_60.short"), sub: tr("options.cup_60.description"), prix: tr("options.cup_60.price") },
                    { value: "cup_200", label: tr("options.cup_200.short"), sub: tr("options.cup_200.description"), prix: tr("options.cup_200.price") },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setReportMode(opt.value)}
                      className={`p-4 rounded-xl border-2 text-left transition ${
                        reportMode === opt.value
                          ? "border-black bg-black text-white"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <div className="font-medium text-sm">{opt.label}</div>
                      <div className={`text-xs mt-0.5 ${reportMode === opt.value ? "text-gray-300" : "text-gray-500"}`}>
                        {opt.sub}
                      </div>
                      <div className={`text-sm font-serif mt-2 ${reportMode === opt.value ? "text-[#c9a961]" : "text-[#c9a961]"}`}>
                        {opt.prix}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Format du rapport */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tr("formatTitle")}</label>
                <div className="flex gap-4">
                  {[
                    { key: "pdf", label: "PDF", icon: Download, desc: tr("formats.pdf") },
                    { key: "word", label: "Word", icon: FileDown, desc: tr("formats.word") },
                  ].map(({ key, label, icon: Icon, desc }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setOutputFormats(f => ({ ...f, [key]: !f[key] }))}
                      className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 text-left transition ${
                        outputFormats[key]
                          ? "border-black bg-black text-white"
                          : "border-gray-200 hover:border-gray-400 text-gray-700"
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm">{label}</div>
                        <div className={`text-xs mt-0.5 ${outputFormats[key] ? "text-gray-300" : "text-gray-500"}`}>{desc}</div>
                      </div>
                      <div className={`ml-auto w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        outputFormats[key] ? "bg-[#c9a961] border-[#c9a961]" : "border-gray-400"
                      }`}>
                        {outputFormats[key] && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Boutons de génération */}
              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={handleGenerateReport}
                  disabled={!!generating || (!outputFormats.pdf && !outputFormats.word)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-black text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
                >
                  {generating === "report" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {generating === "report"
                    ? tr("actions.generating")
                    : outputFormats.pdf && outputFormats.word
                    ? tr("actions.downloadBoth")
                    : outputFormats.word
                    ? tr("actions.downloadWord")
                    : tr("actions.downloadPdf")}
                </button>

                <button
                  type="button"
                  onClick={handleGenerateExcel}
                  disabled={!!generating}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:border-black transition disabled:opacity-50"
                >
                  {generating === "excel" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4" />
                  )}
                  {generating === "excel" ? tr("actions.excelGenerating") : tr("actions.downloadExcel")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}